import React, { useState, useEffect } from 'react';
import { 
  Users, 
  TrendingUp,
  DollarSign,
  Building2,
  Calendar,
  Eye,
  Filter,
  Bus,
  Search,
  MapPin,
  Clock,
  ArrowRight,
  X,
  Loader2,
  Download,
  FileText,
  Edit,
  Save,
  Ticket,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  XCircle,
  CreditCard,
  CheckCircle2
} from 'lucide-react';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { bookingApi, vehicleApi, routeApi, scheduleApi, paymentApi } from '../services/api';
import { getSriLankaDateString, getMinSelectableDate, getMaxSelectableDate } from '../utils/dateUtils';
import { COMPANY_CONFIG } from '../config/config';

const ManagerDashboard = () => {
  const [activeVehicles, setActiveVehicles] = useState(0);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [todayBookings, setTodayBookings] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Recent Bookings filters and data
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  
  // Use Sri Lanka date for all date pickers
  const [filterFromDate, setFilterFromDate] = useState(''); // Default: empty (null) - only filter if filled
  const [filterToDate, setFilterToDate] = useState(''); // Default: empty (null) - only filter if filled
  const [filterTravelFromDate, setFilterTravelFromDate] = useState(''); // Travel date from filter
  const [filterTravelToDate, setFilterTravelToDate] = useState(''); // Travel date to filter
  const [filterRouteId, setFilterRouteId] = useState(''); // Route filter
  const [filterBookingNo, setFilterBookingNo] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('All'); // Payment status filter: All, Pending, Completed
  const [searchTerm, setSearchTerm] = useState(''); // Global search for all columns
  const [filterRoutes, setFilterRoutes] = useState([]); // Routes for filter dropdown

  // Seat Layout View states
  const [selectedViewDate, setSelectedViewDate] = useState(getSriLankaDateString());
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [selectedScheduleDateId, setSelectedScheduleDateId] = useState('');
  const [selectedRouteInfo, setSelectedRouteInfo] = useState(null); // Store selected route info
  const [scheduleDateDetails, setScheduleDateDetails] = useState(null);
  const [seats, setSeats] = useState([]);
  const [seatLayoutLoading, setSeatLayoutLoading] = useState(false);
  const [bookedSeatsData, setBookedSeatsData] = useState([]);
  const [selectedSeatForDetails, setSelectedSeatForDetails] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [bookingDetailsLoading, setBookingDetailsLoading] = useState(false);
  const [bookingSeats, setBookingSeats] = useState([]);
  const [editingSeats, setEditingSeats] = useState(false);
  const [editedSeatNumbers, setEditedSeatNumbers] = useState({});
  const [updatingSeat, setUpdatingSeat] = useState(false);
  const [lastUpdatedSeat, setLastUpdatedSeat] = useState(null); // Track last updated seat: {bookingSeatId, oldSeatNumber, newSeatNumber}
  const [sendingSms, setSendingSms] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded booking rows
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [cancelRemark, setCancelRemark] = useState('');
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    totalSeats: 0,
    availableSeats: 0,
    bookedSeats: 0,
    notAvailableSeats: 0,
    completedPayments: 0,
    pendingPayments: 0
  });

  // Check Payment states
  const [showCheckPaymentModal, setShowCheckPaymentModal] = useState(false);
  const [checkingPayments, setCheckingPayments] = useState(false);
  const [paymentCheckResults, setPaymentCheckResults] = useState([]);
  
  // Payment Gateway Status for table
  const [gatewayStatuses, setGatewayStatuses] = useState({}); // { bookingId: { checking, status, error } }
  const [showGatewayStatusModal, setShowGatewayStatusModal] = useState(false);
  const [selectedGatewayStatus, setSelectedGatewayStatus] = useState(null);



  // Load active vehicles from API
  useEffect(() => {
    const loadActiveVehicles = async () => {
      try {
        setVehiclesLoading(true);
        const response = await vehicleApi.getVehicles({}, true); // Get only active vehicles
        
        // Handle different response structures
        let vehiclesData = [];
        if (response?.vehicles && Array.isArray(response.vehicles)) {
          vehiclesData = response.vehicles;
        } else if (response?.value && Array.isArray(response.value)) {
          vehiclesData = response.value;
        } else if (Array.isArray(response)) {
          vehiclesData = response;
        } else if (response && typeof response === 'object') {
          vehiclesData = [response];
        }
        
        // Count active vehicles (filter by isAvailable if field exists)
        const activeCount = vehiclesData.filter(vehicle => {
          const isAvailable = vehicle.isAvailable !== undefined 
            ? vehicle.isAvailable 
            : (vehicle.IsAvailable !== undefined ? vehicle.IsAvailable : true);
          return isAvailable !== false;
        }).length;
        
        setActiveVehicles(activeCount);
      } catch (error) {
        console.error('Error loading active vehicles:', error);
        // Set default on error
        setActiveVehicles(0);
      } finally {
        setVehiclesLoading(false);
      }
    };

    loadActiveVehicles();
  }, []);

  // Load routes for filter dropdown
  useEffect(() => {
    const loadFilterRoutes = async () => {
      try {
        const response = await routeApi.getRoutes(true); // Get only active routes
        
        let routesData = [];
        if (response?.value && Array.isArray(response.value)) {
          routesData = response.value;
        } else if (Array.isArray(response)) {
          routesData = response;
        } else if (response && typeof response === 'object') {
          routesData = [response];
        }
        
        setFilterRoutes(routesData);
      } catch (error) {
        console.error('Error loading routes for filter:', error);
        setFilterRoutes([]);
      }
    };

    loadFilterRoutes();
  }, []);

  // Get today's date in UTC format (YYYY-MM-DD)
  const getTodayUTCDate = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load today's bookings and revenue from database using dedicated dashboard endpoint
  useEffect(() => {
    const loadTodayStats = async () => {
      try {
        setStatsLoading(true);
        console.log('Fetching today\'s booking statistics from dashboard API...');
        
        // Use dedicated dashboard endpoint (no phone required for managers)
        const response = await bookingApi.getTodayDashboardStats();
        console.log('Today\'s dashboard stats API response:', response);
        
        // Handle different response structures
        let stats = null;
        if (response?.value) {
          stats = response.value;
        } else if (response?.data) {
          stats = response.data;
        } else {
          stats = response;
        }

        // Extract today's bookings count and revenue
        const bookingsCount = stats?.todayBookingsCount || stats?.TodayBookingsCount || stats?.count || 0;
        const revenue = stats?.todayRevenue || stats?.TodayRevenue || stats?.revenue || 0;

        console.log(`Today's bookings count: ${bookingsCount}`);
        console.log(`Today's revenue: Rs ${revenue.toFixed(2)}`);
        
        setTodayBookings(bookingsCount);
        setTodayRevenue(revenue);
      } catch (error) {
        console.error('Error loading today\'s stats from dashboard API:', error);
        
        // Check if endpoint doesn't exist (404)
        if (error.message && error.message.includes('404')) {
          console.error('⚠️ Backend endpoint /api/booking/dashboard/today not found.');
          console.error('Please add the dashboard endpoint to your BookingController.cs');
          console.error('See BACKEND_DASHBOARD_API.md for implementation details.');
        }
        
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
        
        // Set defaults on error
        setTodayBookings(0);
        setTodayRevenue(0);
      } finally {
        setStatsLoading(false);
      }
    };

    loadTodayStats();
  }, []);


  // Download Seat Availability PDF with Route, Schedule, and Passenger Details
  const downloadSeatAvailabilityPDF = async () => {
    if (!selectedScheduleDateId || !selectedRouteId) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a route and date to download the report.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Allow download even if no bookings (will show empty report)
    if (bookedSeatsData.length === 0) {
      const result = await Swal.fire({
        icon: 'info',
        title: 'No Bookings Found',
        text: 'No bookings found for the selected route and date. Do you want to download an empty report?',
        showCancelButton: true,
        confirmButtonText: 'Yes, Download',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6'
      });
      
      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      console.log('Starting PDF generation...', {
        selectedScheduleDateId,
        selectedRouteId,
        bookedSeatsDataLength: bookedSeatsData?.length
      });

      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we prepare your report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Fetch booking details for all booked seats
      const bookingsMap = new Map();
      for (const seatData of bookedSeatsData) {
        if (seatData.bookingID && !bookingsMap.has(seatData.bookingID)) {
          try {
            const booking = await bookingApi.getBooking(seatData.bookingID);
            let bookingData = booking;
            if (booking?.value) bookingData = booking.value;
            else if (booking?.data) bookingData = booking.data;
            bookingsMap.set(seatData.bookingID, bookingData);
          } catch (error) {
            // Handle 404 or other errors gracefully - booking might be deleted
            if (error?.response?.status === 404 || error?.status === 404) {
              console.warn(`Booking ${seatData.bookingID} not found (404). Skipping...`);
            } else {
            console.error(`Error fetching booking ${seatData.bookingID}:`, error);
          }
            // Continue with other bookings even if one fails
          }
        }
      }

      // Create new PDF document in landscape (horizontal) format
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Get route and schedule info
      // Extract start and end cities (same logic as route-management)
      let fromLocation = selectedRouteInfo?.routeName?.split(' → ')[0] || 
                          scheduleDateDetails?.schedule?.route?.fromCityName ||
                          scheduleDateDetails?.schedule?.route?.fromLocation || 
                          scheduleDateDetails?.schedule?.route?.fromCity?.cityName || 'N/A';
      let toLocation = selectedRouteInfo?.routeName?.split(' → ')[1] || 
                       scheduleDateDetails?.schedule?.route?.toCityName ||
                       scheduleDateDetails?.schedule?.route?.toLocation || 
                       scheduleDateDetails?.schedule?.route?.toCity?.cityName || 'N/A';
      
      // Trim whitespace from city names
      fromLocation = fromLocation.trim();
      toLocation = toLocation.trim();
      
      // Debug: Show city names in console with spacing indicator
      console.log('🔍 City Names Debug:');
      console.log('From Location:', JSON.stringify(fromLocation), `[Length: ${fromLocation.length}]`);
      console.log('To Location:', JSON.stringify(toLocation), `[Length: ${toLocation.length}]`);
      if (fromLocation.includes(' ') && fromLocation.trim() !== fromLocation) {
        console.warn('⚠️ From Location has extra spaces!');
      }
      if (toLocation.includes(' ') && toLocation.trim() !== toLocation) {
        console.warn('⚠️ To Location has extra spaces!');
      }
      
      const routeName = `${fromLocation} → ${toLocation}`;
      const departureTime = formatTime(selectedRouteInfo?.departureTime || scheduleDateDetails?.departureTime || '');
      const arrivalTime = formatTime(selectedRouteInfo?.arrivalTime || scheduleDateDetails?.arrivalTime || '');
      const vehicle = selectedRouteInfo?.busNumber || 
                     scheduleDateDetails?.schedule?.vehicle?.registrationNumber || 
                     scheduleDateDetails?.schedule?.vehicle?.busNumber || 'N/A';
      const scheduleName = scheduleDateDetails?.schedule?.scheduleName || 
                          selectedRouteInfo?.scheduleName || 
                          'Standard Schedule';
      const bookingDate = selectedViewDate;
      const reportGeneratedAt = new Date().toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      let yPos = 15;

      // Header with colored background box - height reduced to fit content exactly (no empty space)
      const headerStartY = yPos;
      const headerHeight = 38; // Reduced from 50 to 38 to remove empty space after departure/arrival
      doc.setFillColor(25, 58, 141); // Dark blue (blue-900)
      doc.roundedRect(10, headerStartY, 277, headerHeight, 2, 2, 'F'); // Draw dark blue background first
      
      yPos += 6;
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text(COMPANY_CONFIG.appname, 148.5, yPos, { align: 'center' });
      
      yPos += 7;
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text('Seat Availability & Passenger Details Report', 148.5, yPos, { align: 'center' });
      
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold'); // Set to bold for better visibility
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`${fromLocation} - ${toLocation}`, 148.5, yPos, { align: 'center' });
      
      yPos += 5;
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`Vehicle No: ${vehicle} | Date: ${bookingDate}`, 148.5, yPos, { align: 'center' });
      
      yPos += 5;
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`Departure: ${departureTime || 'N/A'} | Arrival: ${arrivalTime || 'N/A'}`, 148.5, yPos, { align: 'center' });
      
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`Schedule: ${scheduleName}`, 148.5, yPos, { align: 'center' });
      // Show start and end cities directly from database (no arrow symbol)
    
      
      // Show schedule name
 


      // Move yPos to after the blue header box (white area)
      yPos = headerStartY + headerHeight +3; // Start in white area with spacing

      // Passenger Details Section (no header, plain white area)
      yPos += 5; // Small spacing before table

      // Group bookings by bookingID and combine seat numbers
      const bookingGroups = new Map();
      
      // Handle case when there are no bookings
      if (bookedSeatsData.length === 0) {
        console.log('No booked seats data available. Generating empty report.');
      }
      
      for (const seatData of bookedSeatsData) {
        const bookingId = seatData.bookingID;
        if (!bookingId) continue;
        
        const booking = bookingsMap.get(bookingId);
        if (!booking) continue;
        
        if (!bookingGroups.has(bookingId)) {
          const customerName = booking.customer?.customerName || 
                              booking.Customer?.CustomerName ||
                              booking.customer?.CustomerName ||
                              booking.Customer?.customerName || 'N/A';
          const phone = booking.customer?.phone || 
                       booking.Customer?.Phone ||
                       booking.customer?.Phone ||
                       booking.Customer?.phone || 'N/A';
          const gender = booking.customer?.gender ||
                        booking.Customer?.Gender ||
                        booking.customer?.Gender ||
                        booking.Customer?.gender || '-';
          const paymentStatus = booking.paymentStatus || booking.PaymentStatus || 'Pending';
          const bookingDateStr = booking.bookingDate || booking.BookingDate;
          const formattedBookingDate = bookingDateStr 
            ? formatDate(bookingDateStr)
            : 'N/A';
          const totalAmount = booking.totalAmount || booking.TotalAmount || 0;
          const remark = booking.specialRequests || 
                        booking.SpecialRequests || 
                        booking.remark ||
                        booking.Remark || '-';
          
          // Get pickup and dropoff location names
          const pickupLocation = booking.pickupLocationName || 
                                booking.PickupLocationName ||
                                booking.pickupLocation?.cityName ||
                                booking.PickupLocation?.CityName || '-';
          const dropoffLocation = booking.dropoffLocationName || 
                                 booking.DropoffLocationName ||
                                 booking.dropoffLocation?.cityName ||
                                 booking.DropoffLocation?.CityName || '-';
          
          bookingGroups.set(bookingId, {
            bookingId: bookingId.toString(),
            customerName,
            phone,
            gender,
            paymentStatus,
            formattedBookingDate,
            totalAmount,
            remark: remark || '-',
            pickupLocation: pickupLocation || '-',
            dropoffLocation: dropoffLocation || '-',
            seatNumbers: []
          });
        }
        
        // Add seat number to the booking group
        const seatNumber = seatData.seatNumber || 'N/A';
        if (seatNumber !== 'N/A') {
          bookingGroups.get(bookingId).seatNumbers.push(seatNumber);
        }
      }

      // Check if any booking has a remark value (exclude empty, '-', 'N/A', null, undefined)
      let hasRemarks = false;
      for (const bookingData of bookingGroups.values()) {
        const remark = bookingData.remark;
        // Check if remark has actual content (not empty, not '-', not 'N/A', not null, not undefined)
        // Must be a non-empty string after trimming, and not equal to '-' or 'N/A'
        if (remark && 
            typeof remark === 'string') {
          const trimmedRemark = remark.trim();
          if (trimmedRemark !== '' && 
              trimmedRemark !== '-' && 
              trimmedRemark.toUpperCase() !== 'N/A') {
            hasRemarks = true;
            break;
          }
        }
      }
      
      // Prepare table data - one row per booking with comma-separated seat numbers
      const tableData = [];
      
      // If no bookings, add a message row
      if (bookingGroups.size === 0) {
        const row = [
          'N/A',
          'No bookings found',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          '0.00'
        ];
        if (hasRemarks) {
          row.push('No bookings for this route and date');
        }
        tableData.push(row);
      } else {
        for (const bookingData of bookingGroups.values()) {
          const seatNumbersStr = bookingData.seatNumbers.sort((a, b) => {
            const numA = parseInt(a) || 0;
            const numB = parseInt(b) || 0;
            return numA - numB;
          }).join(', ');
          
          // Don't truncate passenger name - use full space (especially when Remarks column is hidden)
          const passengerName = bookingData.customerName || 'N/A';
          // Allow longer names when Remarks column is not shown
          const maxNameLength = hasRemarks ? 20 : 40;
          const truncatedPassengerName = passengerName.length > maxNameLength 
            ? passengerName.substring(0, maxNameLength) 
            : passengerName;
          
          const row = [
            bookingData.bookingId,
            truncatedPassengerName,
            bookingData.phone,
            bookingData.gender,
            seatNumbersStr || 'N/A',
            bookingData.pickupLocation || '-',
            bookingData.dropoffLocation || '-',
            bookingData.paymentStatus,
            bookingData.formattedBookingDate,
            `${bookingData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ];
          
          // Only add remark if there are remarks in the data
          if (hasRemarks) {
            // Show empty string or '-' for empty remarks (don't show '-')
            const remarkValue = bookingData.remark;
            const displayRemark = (remarkValue && remarkValue !== '' && remarkValue !== '-' && remarkValue !== 'N/A' && remarkValue.trim() !== '') 
              ? remarkValue 
              : ''; // Show empty string instead of '-' for empty remarks
            row.push(displayRemark);
          }
          
          tableData.push(row);
        }
      }

      // Comprehensive table with all passenger details
      // Check if autoTable is available, if not use manual table
      let finalY = yPos;
      
      if (doc.autoTable) {
        try {
      // Create header array conditionally based on whether remarks exist
      const headers = ['Booking ID', 'Passenger', 'Phone', 'Gender', 'Seat Numbers', 'Pickup Location', 'Dropoff Location', 'Payment Status', 'Booked', 'Amount'];
      if (hasRemarks) {
        headers.push('Remarks');
      }
      
      // Professional table design: Compact column widths to fit all details
      // A4 Landscape width = 297mm, usable width ~277mm (minus margins)
      console.log('📊 Generating PDF with improved table design...');
      const basePadding = { top: 5, bottom: 5, left: 4, right: 4 }; // Reduced padding for compact fit
      const columnStyles = {
        0: { cellWidth: 20, halign: 'left', cellPadding: basePadding }, // Booking ID
        1: { cellWidth: hasRemarks ? 38 : 50, overflow: 'linebreak', halign: 'left', cellPadding: basePadding }, // Passenger Name - reduced
        2: { cellWidth: 25, cellPadding: basePadding }, // Phone
        3: { cellWidth: 15, cellPadding: basePadding }, // Gender
        4: { cellWidth: 28, cellPadding: basePadding }, // Seat Numbers
        5: { cellWidth: 30, cellPadding: basePadding }, // Pickup Location
        6: { cellWidth: 30, cellPadding: basePadding }, // Dropoff Location
        7: { cellWidth: 25, cellPadding: basePadding }, // Payment Status
        8: { cellWidth: 22, cellPadding: basePadding }, // Booked (date only - 10 chars)
        9: { cellWidth: 22, halign: 'right', cellPadding: basePadding } // Amount - right aligned for numbers
      };
      
      // Only add Remarks column style if there are remarks
      if (hasRemarks) {
        columnStyles[10] = { cellWidth: 30, cellPadding: basePadding }; // Remarks - reduced
      }
      
      doc.autoTable({
        startY: yPos,
            head: [headers],
        body: tableData,
        headStyles: {
          fillColor: [30, 64, 175], // Darker blue for better contrast
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10, // Slightly larger for visibility
          lineWidth: 0.5,
          cellPadding: basePadding,
          halign: 'right' // Right align headers (except first column)
        },
        bodyStyles: {
          fontSize: 8.5,
          lineWidth: 0.5,
          lineColor: [200, 200, 200], // Light gray lines
          cellPadding: basePadding,
          textColor: [51, 51, 51], // Dark gray text for better readability
          halign: 'left',
          fontStyle: 'normal'
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251], // Very light gray for subtle row separation
          cellPadding: basePadding,
          halign: 'left'
        },
        columnStyles: columnStyles,
            margin: { top: yPos, left: 10, right: 10 },
            // Use full width in landscape mode (A4 landscape = 297mm width)
            tableWidth: 'auto',
        styles: {
          lineColor: [200, 200, 200],
          lineWidth: 0.5,
          overflow: 'linebreak',
          cellPadding: basePadding,
          halign: 'left'
        },
            didDrawPage: function (data) {
              // Add footer with page number and generation date
              doc.setFontSize(7);
              doc.setTextColor(128, 128, 128);
              const pageText = `Page ${data.pageNumber} of ${data.pageCount || 1}`;
              doc.text(
                pageText,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 8,
                { align: 'center' }
              );
              doc.text(
                `Generated on: ${reportGeneratedAt}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 4,
                { align: 'center' }
              );
            }
          });
          
          // Get final Y position from autoTable if available
          if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
            finalY = doc.lastAutoTable.finalY;
          } else {
            // Estimate final Y position
            finalY = yPos + (tableData.length * 8) + 20;
          }
        } catch (error) {
          console.error('Error using autoTable:', error);
          console.error('Error details:', error.message, error.stack);
          // Fall through to manual table
          finalY = yPos;
        }
      }
      
      // If autoTable failed or is not available, use manual table
      if (finalY === yPos || !doc.autoTable) {
        // Fallback: Create simple table manually with improved design
        console.warn('Using manual table generation');
        let tableY = yPos;
        const rowHeight = 6; // Compact row height
        // Compact column widths to fit all details on page (A4 landscape = 297mm, usable ~277mm)
        const colWidths = [20, hasRemarks ? 38 : 50, 25, 15, 28, 30, 30, 25, 22, 22]; // Base columns - compact (Booked date reduced from 28 to 22)
        if (hasRemarks) {
          colWidths.push(30); // Add Remarks column width - compact
        }
        let xPos = 10; // Start from margin
        
        // Draw header with improved styling
        doc.setFont(undefined, 'bold');
        doc.setFontSize(10); // Larger header font
        doc.setTextColor(255, 255, 255); // White text
        doc.setFillColor(30, 64, 175); // Darker blue background
        // Draw header background rectangle
        doc.roundedRect(10, tableY - 5, 277, rowHeight + 2, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        
        const headers = ['ID', 'Passenger', 'Phone', 'Gender', 'Seat Numbers', 'Pickup', 'Dropoff', 'Payment ', 'Booked', 'Amount'];
        if (hasRemarks) {
          headers.push('Remarks');
        }
        headers.forEach((header, i) => {
          // Right align headers except first
          const align = i === 0 ? 'left' : 'right';
          doc.text(header, xPos + (i === 0 ? 3 : colWidths[i] - 3), tableY, { align: align });
          xPos += colWidths[i];
        });
        tableY += rowHeight + 3;
        
        // Reset text color and draw header line
        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(10, tableY, 287, tableY);
        tableY += 5; // Gap after header line
        
        // Draw rows with improved styling
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8.5); // Slightly larger body font
        doc.setTextColor(51, 51, 51); // Dark gray text
        tableData.forEach((row, rowIndex) => {
          if (tableY > doc.internal.pageSize.height - 20) {
            doc.addPage();
            tableY = 20;
          }
          
          // Alternate row background color
          if (rowIndex % 2 === 1) {
            doc.setFillColor(249, 250, 251);
            doc.roundedRect(10, tableY - 4, 277, rowHeight + 1, 1, 1, 'F');
          }
          
          xPos = 10;
          row.forEach((cell, colIndex) => {
            const cellText = String(cell || '').trim();
            // Adjust maxLength for compact columns - allow more characters to fit
            const maxLength = colIndex === 1 ? 30 : (colIndex === 4 ? 35 : (colIndex === 5 || colIndex === 6 ? 32 : (colIndex === 10 ? 30 : 25)));
            const displayText = cellText.length > maxLength ? cellText.substring(0, maxLength - 3) + '...' : cellText;
            
            // Right align for amount column (last data column), left for others
            const align = (colIndex === 9) ? 'right' : 'left';
            doc.text(displayText, xPos + (align === 'right' ? colWidths[colIndex] - 3 : 3), tableY, { align: align });
            xPos += colWidths[colIndex];
          });
          tableY += rowHeight + 2; // Slightly more spacing between rows
        });
        
        // Update finalY for manual table
        finalY = tableY + 5;
      }

      // Calculate summary statistics
      const totalBookings = bookingGroups.size;
      const totalBookedSeats = bookedSeatsData.length;
      
      // Count completed and pending payments
      let completedPaymentCount = 0;
      let pendingPaymentCount = 0;
      
      for (const bookingData of bookingGroups.values()) {
        const status = String(bookingData.paymentStatus).toLowerCase();
        if (status === 'completed' || status === '1' || bookingData.paymentStatus === 1 || bookingData.paymentStatus === 'Completed') {
          completedPaymentCount++;
        } else {
          pendingPaymentCount++;
        }
      }
      
      // Add Summary Statistics Section
      yPos = finalY + 10;
      if (yPos > doc.internal.pageSize.height - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      // Summary Statistics Box
      doc.setFillColor(239, 246, 255); // Blue-50
      doc.roundedRect(10, yPos, 277, 25, 2, 2, 'F');
      
      yPos += 5;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Summary Statistics', 15, yPos);
      
      yPos += 7;
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      
      const summaryLeftX = 15;
      const summaryRightX = 150;
      let summaryY = yPos;
      
      doc.setFont(undefined, 'bold');
      doc.text('Total Bookings:', summaryLeftX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.text(totalBookings.toString(), summaryLeftX + 35, summaryY);
      
      doc.setFont(undefined, 'bold');
      doc.text('Total Booked Seats:', summaryRightX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.text(totalBookedSeats.toString(), summaryRightX + 40, summaryY);
      
      summaryY += 5;
      doc.setFont(undefined, 'bold');
      doc.text('Pending Payment Count:', summaryLeftX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(234, 179, 8); // Yellow
      doc.text(pendingPaymentCount.toString(), summaryLeftX + 50, summaryY);
      doc.setTextColor(0, 0, 0);
      
      doc.setFont(undefined, 'bold');
      doc.text('Completed Payment Count:', summaryRightX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(34, 197, 94); // Green
      doc.text(completedPaymentCount.toString(), summaryRightX + 55, summaryY);
      doc.setTextColor(0, 0, 0);
      
      // Add footer to last page if not already added by autoTable
      yPos = summaryY + 15;
      if (yPos > doc.internal.pageSize.height - 20) {
        yPos = doc.internal.pageSize.height - 15;
      }
      doc.setFontSize(7);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Generated on: ${reportGeneratedAt}`,
        doc.internal.pageSize.width / 2,
        yPos,
        { align: 'center' }
      );

      // Save PDF
      const fileName = `Route_Booking_Details_${selectedViewDate}_${selectedRouteId}.pdf`;
      
      console.log('Attempting to save PDF:', fileName);
      
      try {
        // Save the PDF using jsPDF's save method
      doc.save(fileName);
        console.log('PDF saved successfully using doc.save()');
        
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Download Complete',
          text: 'PDF report with route, schedule, and passenger details downloaded successfully!',
          confirmButtonColor: '#2563eb'
        });
      } catch (saveError) {
        console.error('Error saving PDF with doc.save():', saveError);
        // Try alternative method - create blob and download
        try {
          console.log('Trying alternative blob download method...');
          const pdfBlob = doc.output('blob');
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          
          // Clean up after a short delay
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
          
          console.log('PDF downloaded successfully using blob method');

      Swal.close();
      Swal.fire({
        icon: 'success',
        title: 'Download Complete',
        text: 'PDF report downloaded successfully!',
        confirmButtonColor: '#2563eb'
      });
        } catch (blobError) {
          console.error('Error with blob download:', blobError);
          throw blobError;
        }
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        selectedScheduleDateId,
        selectedRouteId,
        bookedSeatsDataLength: bookedSeatsData?.length
      });
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error Generating PDF',
        html: `Failed to generate PDF. Please check the console for details.<br/><br/>Error: ${error.message || 'Unknown error'}`,
        confirmButtonColor: '#dc2626'
      });
    }
  };

  // Function to load available routes for selected date (called by search button)
  const loadRoutesForDate = async () => {
    if (!selectedViewDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Date Required',
        text: 'Please select a date to search for seat availability.',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    try {
      setRoutesLoading(true);
      // Clear previous selections when searching
      setSelectedRouteId('');
      setSelectedScheduleId('');
      setSelectedScheduleDateId('');
      setSelectedRouteInfo(null);
      setSeats([]);
      setScheduleDateDetails(null);
      
      // Get all active routes
      const routesResponse = await routeApi.getRoutes(true);
      let allRoutes = [];
      
      if (routesResponse?.value && Array.isArray(routesResponse.value)) {
        allRoutes = routesResponse.value;
      } else if (Array.isArray(routesResponse)) {
        allRoutes = routesResponse;
      }

      // Get all schedules
      const schedulesResponse = await scheduleApi.getSchedules();
      let allSchedules = [];
      
      if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
        allSchedules = schedulesResponse.value;
      } else if (Array.isArray(schedulesResponse)) {
        allSchedules = schedulesResponse;
      }

      // Filter schedules to only active ones
      const activeSchedules = allSchedules.filter(s => s.isActive !== false);

      // Get schedules for each route and filter by date
      const routesWithSchedules = [];
      
      for (const route of allRoutes) {
        try {
          const routeId = route.routeID || route.id;
          const routeSchedules = activeSchedules.filter(s => 
            (s.routeID || s.routeId) === routeId
          );

          // Check if any schedule has a schedule date for the selected date
          for (const schedule of routeSchedules) {
            const scheduleId = schedule.scheduleID || schedule.id;
            
            try {
              const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(scheduleId);
              let scheduleDates = [];
              
              if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
                scheduleDates = scheduleDatesResponse.value;
              } else if (Array.isArray(scheduleDatesResponse)) {
                scheduleDates = scheduleDatesResponse;
              }

              const matchingScheduleDate = scheduleDates.find(sd => {
                const dateStr = sd.date?.split('T')[0] || sd.date;
                return dateStr === selectedViewDate;
              });

              if (matchingScheduleDate) {
                // Get route city names
                const fromCity = route.fromCity?.cityName || route.fromCityName || route.fromLocation || 'Unknown';
                const toCity = route.toCity?.cityName || route.toCityName || route.toLocation || 'Unknown';
                
                routesWithSchedules.push({
                  routeId: routeId,
                  scheduleId: scheduleId,
                  scheduleDateId: matchingScheduleDate.scheduleDateID || matchingScheduleDate.scheduleDateId || matchingScheduleDate.id,
                  routeName: `${fromCity} → ${toCity}`,
                  scheduleName: schedule.scheduleName || 'Standard',
                  departureTime: matchingScheduleDate.departureTime || schedule.departureTime || 'N/A',
                  arrivalTime: matchingScheduleDate.arrivalTime || schedule.arrivalTime || 'N/A',
                  busNumber: schedule.vehicle?.registrationNumber || schedule.vehicle?.busNumber || schedule.busNumber || 'N/A'
                });
                break; // Only add route once
              }
            } catch (scheduleDateError) {
              // Skip schedules with errors fetching dates
              continue;
            }
          }
        } catch (error) {
          // Skip routes with errors
          continue;
        }
      }

      setAvailableRoutes(routesWithSchedules);
      
      if (routesWithSchedules.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Routes Found',
          text: `No routes available for the selected date: ${selectedViewDate}`,
          confirmButtonColor: '#2563eb',
        });
      }
    } catch (error) {
      console.error('Error loading routes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error Loading Routes',
        text: error.message || 'Failed to load routes. Please try again.',
        confirmButtonColor: '#dc2626',
      });
      setAvailableRoutes([]);
    } finally {
      setRoutesLoading(false);
    }
  };

  // Load seat layout when route is selected
  useEffect(() => {
    const loadSeatLayout = async () => {
      if (!selectedScheduleDateId) {
        setSeats([]);
        setScheduleDateDetails(null);
        setSummaryStats({
          totalSeats: 0,
          availableSeats: 0,
          bookedSeats: 0,
          notAvailableSeats: 0,
          completedPayments: 0,
          pendingPayments: 0
        });
        return;
      }

      try {
        setSeatLayoutLoading(true);
        const response = await scheduleApi.getScheduleDate(selectedScheduleDateId);
        const scheduleDateData = response?.value || response;

        if (scheduleDateData) {
          setScheduleDateDetails(scheduleDateData);

          // Get booked seats with gender
          // Extract booked seat numbers with gender from response
          // Check multiple possible response structures
          let bookedSeatsArray = null;
          
          if (scheduleDateData?.bookedSeatNumbers) {
            bookedSeatsArray = scheduleDateData.bookedSeatNumbers;
          } else if (scheduleDateData?.data?.bookedSeatNumbers) {
            bookedSeatsArray = scheduleDateData.data.bookedSeatNumbers;
          } else if (Array.isArray(scheduleDateData)) {
            // If response is directly an array
            bookedSeatsArray = scheduleDateData;
          }
          
          let bookedSeatNumbers = [];
          if (bookedSeatsArray && Array.isArray(bookedSeatsArray)) {
            // Check if bookedSeatNumbers is array of objects with gender or just strings
            if (bookedSeatsArray.length > 0 && typeof bookedSeatsArray[0] === 'object' && bookedSeatsArray[0] !== null) {
              // Array of objects with seatNumber and gender
              bookedSeatNumbers = bookedSeatsArray;
              // Debug: Log the raw booked seats array to see structure
              console.log('Booked seats array (objects):', bookedSeatsArray);
            } else {
              // Array of strings (backward compatibility)
              bookedSeatNumbers = bookedSeatsArray.map(s => ({
                seatNumber: s.toString().trim(),
                gender: null
              }));
            }
          }
          
          // Debug: Log what we extracted
          if (bookedSeatNumbers.length > 0) {
            console.log('Extracted booked seat numbers:', bookedSeatNumbers);
          }

          // Get vehicle seat layout
          const vehicle = scheduleDateData.schedule?.vehicle || scheduleDateData.vehicle;
          const seatLayout = vehicle?.seatLayout || '4x12';
          const totalSeats = vehicle?.totalSeats || 48;
          
          // Handle notAvailableSeats - could be string, array, or comma-separated
          // Check multiple possible locations: vehicle.notAvailableSeatNumbers (array), vehicle.notAvailableSeats (string or array), or scheduleDateData.notAvailableSeatNumbers
          let notAvailableSeats = [];
          
          // First, try vehicle.notAvailableSeatNumbers (array from API)
          if (vehicle?.notAvailableSeatNumbers && Array.isArray(vehicle.notAvailableSeatNumbers)) {
            notAvailableSeats = vehicle.notAvailableSeatNumbers.map(s => s.toString().trim()).filter(s => s !== '' && s !== '0');
          }
          // Then try vehicle.notAvailableSeats (string or array)
          else if (vehicle?.notAvailableSeats) {
            if (Array.isArray(vehicle.notAvailableSeats)) {
              notAvailableSeats = vehicle.notAvailableSeats.map(s => s.toString().trim()).filter(s => s !== '' && s !== '0');
            } else if (typeof vehicle.notAvailableSeats === 'string') {
              // Parse comma-separated string like "1,5,11"
              notAvailableSeats = vehicle.notAvailableSeats
                .split(',')
                .map(s => s.trim())
                .filter(s => s !== '' && s !== '0');
            }
          }
          // Try alternative field name vehicle.notAvailableSeat
          else if (vehicle?.notAvailableSeat) {
            if (typeof vehicle.notAvailableSeat === 'string') {
              notAvailableSeats = vehicle.notAvailableSeat
                .split(',')
                .map(s => s.trim())
                .filter(s => s !== '' && s !== '0');
            }
          }
          // Also check at root level (from GetAvailableSeats endpoint)
          else if (scheduleDateData?.notAvailableSeatNumbers && Array.isArray(scheduleDateData.notAvailableSeatNumbers)) {
            notAvailableSeats = scheduleDateData.notAvailableSeatNumbers.map(s => s.toString().trim()).filter(s => s !== '' && s !== '0');
          }
          
          // Debug: Log not available seats
          console.log('Not Available Seats extracted:', notAvailableSeats);

          // Format booked seats for generateSeats function
          // generateSeats expects array of objects with seatNumber and gender properties
          const formattedBookedSeats = bookedSeatNumbers.map(seat => {
            if (typeof seat === 'string') {
              return {
                seatNumber: seat.toString().trim(),
                gender: null,
                customerID: null,
                bookingID: null
              };
            }
            // Extract gender - check multiple possible field names and formats
            let genderValue = seat.gender || seat.Gender || seat.genderValue || null;
            if (genderValue) {
              genderValue = genderValue.toString().trim();
            }
            
            // Extract customerID
            const customerID = seat.customerID || seat.CustomerID || seat.customerId || null;
            
            // Extract bookingID
            const bookingID = seat.bookingID || seat.BookingID || seat.bookingId || null;
            
            // Extract paymentStatus
            const paymentStatus = seat.paymentStatus || seat.PaymentStatus || seat.paymentstatus || null;
            
            const seatNumber = (seat.seatNumber || seat.SeatNumber || seat).toString().trim();
            
            // Debug: Log each seat's gender extraction with CustomerID, BookingID, and PaymentStatus
            console.log(`Frontend: Processing seat ${seatNumber}, CustomerID=${customerID}, BookingID=${bookingID}, PaymentStatus=${paymentStatus}, raw gender="${seat.gender || seat.Gender || 'null'}", extracted="${genderValue}"`);
            
            return {
              seatNumber: seatNumber,
              gender: genderValue,
              customerID: customerID,
              bookingID: bookingID,
              paymentStatus: paymentStatus
            };
          });
          
          // Debug: Log formatted booked seats to verify gender, CustomerID, and PaymentStatus
          if (formattedBookedSeats.length > 0) {
            console.log('=== Formatted booked seats with gender, CustomerID, and PaymentStatus ===');
            console.log('Total booked seats:', formattedBookedSeats.length);
            formattedBookedSeats.forEach(seat => {
              console.log(`Seat ${seat.seatNumber}: CustomerID=${seat.customerID}, BookingID=${seat.bookingID}, Gender=${seat.gender}, PaymentStatus="${seat.paymentStatus}" (type: ${typeof seat.paymentStatus})`);
            });
            // Specifically check seat 15 if it exists
            const seat15 = formattedBookedSeats.find(s => s.seatNumber === '15');
            if (seat15) {
              console.log('*** Seat 15 details:', seat15);
              console.log(`*** Seat 15: CustomerID=${seat15.customerID}, BookingID=${seat15.bookingID}, Gender=${seat15.gender}, PaymentStatus="${seat15.paymentStatus}" (type: ${typeof seat15.paymentStatus})`);
            }
            console.log('========================================================');
          }

          // Store booked seats data for seat click handler
          setBookedSeatsData(formattedBookedSeats);

          // Generate seats
          const generatedSeats = generateSeats(seatLayout, formattedBookedSeats, notAvailableSeats);
          setSeats(generatedSeats);

          // Calculate summary statistics
          const bookedSeats = generatedSeats.filter(s => !s.isAvailable && !s.isNotAvailable).length;
          const availableSeats = generatedSeats.filter(s => s.isAvailable).length;
          const notAvailableSeatsCount = generatedSeats.filter(s => s.isNotAvailable).length;
          
          // Debug: Log seat counts
          console.log(`Seat counts - Total: ${generatedSeats.length}, Available: ${availableSeats}, Booked: ${bookedSeats}, Not Available: ${notAvailableSeatsCount}`);
          
          // Count completed and pending payments using the same data source as seat layout
          // Use formattedBookedSeats which has the payment status from the API
          let completedPayments = 0;
          let pendingPayments = 0;
          
          // Get unique booking IDs to count distinct bookings (not seats)
          const uniqueBookings = new Set();
          const bookingPaymentStatus = new Map(); // Map bookingID to paymentStatus
          
          formattedBookedSeats.forEach(seat => {
            const bookingID = seat.bookingID;
            if (bookingID) {
              uniqueBookings.add(bookingID);
              // Store payment status for this booking (if not already stored or if this one is more recent)
              if (!bookingPaymentStatus.has(bookingID)) {
                bookingPaymentStatus.set(bookingID, seat.paymentStatus);
              }
            }
          });
          
          // Count completed and pending bookings
          bookingPaymentStatus.forEach((paymentStatus, bookingID) => {
            const statusLower = paymentStatus ? paymentStatus.toString().toLowerCase().trim() : '';
            if (statusLower === 'completed' || statusLower === '1' || paymentStatus === 1 || paymentStatus === '1') {
              completedPayments++;
            } else if (statusLower === 'pending' || statusLower === '0' || paymentStatus === 0 || paymentStatus === '0' || !paymentStatus) {
              pendingPayments++;
            }
          });
          
          // Debug: Log payment status counts
          console.log(`=== Payment Status Counts (from seat data) ===`);
          console.log(`Total unique bookings: ${uniqueBookings.size}`);
          bookingPaymentStatus.forEach((paymentStatus, bookingID) => {
            console.log(`BookingID ${bookingID}: PaymentStatus="${paymentStatus}" (type: ${typeof paymentStatus})`);
          });
          console.log(`Completed payments: ${completedPayments}`);
          console.log(`Pending payments: ${pendingPayments}`);
          console.log('======================================================');

          setSummaryStats({
            totalSeats,
            availableSeats,
            bookedSeats,
            notAvailableSeats: notAvailableSeatsCount,
            completedPayments,
            pendingPayments
          });
        }
      } catch (error) {
        console.error('Error loading seat layout:', error);
        setSeats([]);
        setScheduleDateDetails(null);
      } finally {
        setSeatLayoutLoading(false);
      }
    };

    loadSeatLayout();
  }, [selectedScheduleDateId, selectedViewDate]);

  // Auto-load routes when date changes or on initial load
  useEffect(() => {
    if (selectedViewDate) {
      loadRoutesForDate();
    }
  }, [selectedViewDate]);

  // Search/Filter bookings by calling backend API with filter parameters
  const handleSearchBookings = async () => {
    try {
      setBookingsLoading(true);
      setFilteredBookings([]);

      // Build filter object to pass to API (only include non-empty values)
      const filters = {};
      if (filterFromDate && filterFromDate.trim() !== '') {
        filters.fromDate = filterFromDate; // Format: YYYY-MM-DD (Booking Date)
      }
      if (filterToDate && filterToDate.trim() !== '') {
        filters.toDate = filterToDate; // Format: YYYY-MM-DD (Booking Date)
      }
      if (filterTravelFromDate && filterTravelFromDate.trim() !== '') {
        filters.travelFromDate = filterTravelFromDate; // Format: YYYY-MM-DD (Travel Date)
      }
      if (filterTravelToDate && filterTravelToDate.trim() !== '') {
        filters.travelToDate = filterTravelToDate; // Format: YYYY-MM-DD (Travel Date)
      }
      if (filterRouteId && filterRouteId.trim() !== '') {
        const routeId = parseInt(filterRouteId);
        if (!isNaN(routeId) && routeId > 0) {
          filters.routeId = routeId; // Route ID
        }
      }
      if (filterBookingNo && filterBookingNo.trim() !== '') {
        const bookingId = parseInt(filterBookingNo);
        if (!isNaN(bookingId) && bookingId > 0) {
          filters.bookingId = bookingId; // Booking ID
        }
      }

      console.log('🔍 Searching bookings with filters:', filters);

      // Call backend API with filter parameters
      const response = await bookingApi.getAllBookings(filters);
      
      // Handle different response structures
      let bookingsData = [];
      if (Array.isArray(response)) {
        bookingsData = response;
      } else if (response?.value && Array.isArray(response.value)) {
        bookingsData = response.value;
      } else if (response?.data && Array.isArray(response.data)) {
        bookingsData = response.data;
      } else if (response?.bookings && Array.isArray(response.bookings)) {
        bookingsData = response.bookings;
      }

      console.log('✅ Filtered bookings received:', bookingsData.length);
      setFilteredBookings(bookingsData);
      
      // Also update allBookings for future use
      if (bookingsData.length > 0) {
        setAllBookings(bookingsData);
        
        // Automatically check payment gateway status for all bookings
        checkPaymentGatewayStatuses(bookingsData);
      }
    } catch (error) {
      console.error('Error searching bookings:', error);
      Swal.fire({
        icon: 'error',
        title: 'Search Failed',
        text: error.message || 'Failed to search bookings. Please try again.',
        confirmButtonColor: '#dc2626',
      });
      setFilteredBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  // Automatically check payment gateway status for all bookings
  const checkPaymentGatewayStatuses = async (bookings) => {
    if (!bookings || bookings.length === 0) return;
    
    // Initialize all bookings as checking
    const initialStatuses = {};
    bookings.forEach(booking => {
      const bookingId = booking.bookingID || booking.BookingID || booking.id;
      if (bookingId) {
        initialStatuses[bookingId] = { checking: true, status: null, error: null };
      }
    });
    setGatewayStatuses(initialStatuses);
    
    // Check payment status for each booking (with delay to avoid overwhelming the API)
    for (let i = 0; i < bookings.length; i++) {
      const booking = bookings[i];
      const bookingId = booking.bookingID || booking.BookingID || booking.id;
      
      if (!bookingId) continue;
      
      // Add small delay between requests to avoid rate limiting
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay between requests
      }
      
      try {
        const response = await paymentApi.checkPaymentStatus(bookingId);
        const data = response?.value || response?.data || response;
        
        setGatewayStatuses(prev => ({
          ...prev,
          [bookingId]: { 
            checking: false, 
            status: data.isPaymentSuccessful ? 'Success' : 'Failed',
            error: null,
            details: data
          }
        }));
      } catch (error) {
        setGatewayStatuses(prev => ({
          ...prev,
          [bookingId]: { 
            checking: false, 
            status: null, 
            error: error.response?.data?.message || error.message || 'Failed to check'
          }
        }));
      }
    }
  };

  // Clear filters and reset to today's date
  const handleClearFilters = () => {
    setFilterFromDate(''); // Reset to empty (null)
    setFilterToDate(''); // Reset to empty (null)
    setFilterTravelFromDate('');
    setFilterTravelToDate('');
    setFilterRouteId('');
    setFilterBookingNo('');
    setFilterPaymentStatus('All');
    setSearchTerm('');
    setFilteredBookings([]);
    setGatewayStatuses({}); // Clear gateway statuses when filters are cleared
  };

  // Filter bookings based on search term (searches across all columns)
  const getFilteredBookingsBySearch = () => {
    let result = filteredBookings;
    
    // Apply payment status filter
    if (filterPaymentStatus && filterPaymentStatus !== 'All') {
      result = result.filter((booking) => {
        const paymentStatus = (booking.paymentStatus || booking.PaymentStatus || 'Pending').toString().toLowerCase();
        if (filterPaymentStatus === 'Pending') {
          return paymentStatus === 'pending';
        } else if (filterPaymentStatus === 'Completed') {
          return paymentStatus === 'completed' || paymentStatus === 'paid';
        }
        return true;
      });
    }
    
    // Apply search term filter
    if (!searchTerm || searchTerm.trim() === '') {
      return result;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    
    return result.filter((booking) => {
      const bookingId = String(booking.bookingID || booking.BookingID || booking.id || '').toLowerCase();
      const travelDate = formatDate(booking.travelDate || booking.TravelDate).toLowerCase();
      const bookingDate = formatDate(booking.bookingDate || booking.BookingDate).toLowerCase();
      const phone = (booking.customerPhone || booking.CustomerPhone || booking.phone || booking.Customer?.Phone || '').toLowerCase();
      const passengerName = (booking.customer?.fullName || 
                            booking.Customer?.FullName ||
                            booking.customerName ||
                            booking.CustomerName ||
                            booking.Customer?.CustomerName || '').toLowerCase();
      // Created By - shows the name of the user who created the booking (logged-in user)
      const createdByUser = booking.user?.fullName || 
                           booking.User?.FullName ||
                           booking.user?.FullName ||
                           booking.User?.fullName ||
                           booking.createdByName ||
                           booking.CreatedByName ||
                           'N/A';
      const createdBy = createdByUser !== 'N/A' ? createdByUser.toLowerCase() : 'n/a'; // Use lowercase for search
      const totalAmount = String(booking.totalAmount || booking.TotalAmount || 0).toLowerCase();
      const bookingStatus = (booking.status || booking.Status || 'Confirmed').toLowerCase();
      const paymentStatus = (booking.paymentStatus || booking.PaymentStatus || 'Pending').toLowerCase();
      
      // Extract route information
      const fromLocation = (booking.schedule?.route?.fromLocation || 
                           booking.Schedule?.Route?.FromLocation ||
                           booking.schedule?.route?.fromCityName ||
                           booking.Schedule?.Route?.FromCityName || '').toLowerCase();
      const toLocation = (booking.schedule?.route?.toLocation || 
                         booking.Schedule?.Route?.ToLocation ||
                         booking.schedule?.route?.toCityName ||
                         booking.Schedule?.Route?.ToCityName || '').toLowerCase();
      
      // Extract bus number
      const busNumber = (booking.schedule?.vehicle?.registrationNumber || 
                        booking.Schedule?.Vehicle?.RegistrationNumber ||
                        booking.schedule?.vehicle?.registrationNo ||
                        booking.Schedule?.Vehicle?.RegistrationNo || '').toLowerCase();
      
      // Extract departure and arrival times
      const departureTime = formatTime(booking.scheduleDate?.departureTime || 
                                       booking.ScheduleDate?.DepartureTime ||
                                       booking.scheduleDate?.departureTime ||
                                       booking.ScheduleDate?.DepartureTime || '').toLowerCase();
      const arrivalTime = formatTime(booking.scheduleDate?.arrivalTime || 
                                     booking.ScheduleDate?.ArrivalTime ||
                                     booking.scheduleDate?.arrivalTime ||
                                     booking.ScheduleDate?.ArrivalTime || '').toLowerCase();
      
      // Search across all fields
      return bookingId.includes(searchLower) ||
             travelDate.includes(searchLower) ||
             bookingDate.includes(searchLower) ||
             phone.includes(searchLower) ||
             passengerName.includes(searchLower) ||
             createdBy.includes(searchLower) ||
             totalAmount.includes(searchLower) ||
             bookingStatus.includes(searchLower) ||
             paymentStatus.includes(searchLower) ||
             fromLocation.includes(searchLower) ||
             toLocation.includes(searchLower) ||
             busNumber.includes(searchLower) ||
             departureTime.includes(searchLower) ||
             arrivalTime.includes(searchLower);
    });
  };

  // Format revenue for display (no currency symbol)
  const formatRevenue = (amount) => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Format date for display as yyyy-MM-dd
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Format time for display (HH:mm)
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle TimeSpan format (HH:mm:ss) or string format
      if (typeof timeString === 'string') {
        // If it's already in HH:mm format, return it
        if (timeString.match(/^\d{2}:\d{2}$/)) {
          return timeString;
        }
        // Try to parse as time
        const time = new Date(`2000-01-01T${timeString}`);
        if (!isNaN(time.getTime())) {
          const hours = String(time.getHours()).padStart(2, '0');
          const minutes = String(time.getMinutes()).padStart(2, '0');
          return `${hours}:${minutes}`;
        }
      }
      return timeString;
    } catch (error) {
      return 'N/A';
    }
  };

  // Generate seats function (similar to BusBooking.js)
  const generateSeats = (seatLayout, bookedSeats = [], notAvailableSeats = []) => {
    const seats = [];
    
    const notAvailableSeatNumbers = notAvailableSeats
      .map(seat => seat.toString().trim())
      .filter(seat => seat !== '')
      .map(seat => parseInt(seat))
      .filter(seat => !isNaN(seat));
    
    // Check if this is the special 51 Layout
    if (seatLayout && seatLayout.toString().toLowerCase().includes('51 layout')) {
      // 51 Layout: Special arrangement - only actual seats, no empty cells
      // Seat 51 in the middle between left and right blocks
      const seatPositions = [
        // Row 1: Seat 51 in middle
        { row: 1, col: 1, seatNum: 47 }, { row: 1, col: 2, seatNum: 48 },
        { row: 1, col: 3, seatNum: 51 }, // Seat 51 in middle
        { row: 1, col: 4, seatNum: 50 }, { row: 1, col: 5, seatNum: 49 },
        // Row 2
        { row: 2, col: 1, seatNum: 41 }, { row: 2, col: 2, seatNum: 42 },
        { row: 2, col: 3, seatNum: null }, // Aisle (skip)
        { row: 2, col: 4, seatNum: 46 }, { row: 2, col: 5, seatNum: 45 },
        // Row 3
        { row: 3, col: 1, seatNum: 37 }, { row: 3, col: 2, seatNum: 38 },
        { row: 3, col: 3, seatNum: null }, // Aisle (skip)
        { row: 3, col: 4, seatNum: 44 }, { row: 3, col: 5, seatNum: 43 },
        // Row 4
        { row: 4, col: 1, seatNum: 33 }, { row: 4, col: 2, seatNum: 34 },
        { row: 4, col: 3, seatNum: null }, // Aisle (skip)
        { row: 4, col: 4, seatNum: 40 }, { row: 4, col: 5, seatNum: 39 },
        // Row 5
        { row: 5, col: 1, seatNum: 29 }, { row: 5, col: 2, seatNum: 30 },
        { row: 5, col: 3, seatNum: null }, // Aisle (skip)
        { row: 5, col: 4, seatNum: 36 }, { row: 5, col: 5, seatNum: 35 },
        // Row 6
        { row: 6, col: 1, seatNum: 25 }, { row: 6, col: 2, seatNum: 26 },
        { row: 6, col: 3, seatNum: null }, // Aisle (skip)
        { row: 6, col: 4, seatNum: 32 }, { row: 6, col: 5, seatNum: 31 },
        // Row 7
        { row: 7, col: 1, seatNum: 21 }, { row: 7, col: 2, seatNum: 22 },
        { row: 7, col: 3, seatNum: null }, // Aisle (skip)
        { row: 7, col: 4, seatNum: 28 }, { row: 7, col: 5, seatNum: 27 },
        // Row 8
        { row: 8, col: 1, seatNum: 17 }, { row: 8, col: 2, seatNum: 18 },
        { row: 8, col: 3, seatNum: null }, // Aisle (skip)
        { row: 8, col: 4, seatNum: 24 }, { row: 8, col: 5, seatNum: 23 },
        // Row 9
        { row: 9, col: 1, seatNum: 13 }, { row: 9, col: 2, seatNum: 14 },
        { row: 9, col: 3, seatNum: null }, // Aisle (skip)
        { row: 9, col: 4, seatNum: 20 }, { row: 9, col: 5, seatNum: 19 },
        // Row 10
        { row: 10, col: 1, seatNum: 9 }, { row: 10, col: 2, seatNum: 10 },
        { row: 10, col: 3, seatNum: null }, // Aisle (skip)
        { row: 10, col: 4, seatNum: 16 }, { row: 10, col: 5, seatNum: 15 },
        // Row 11
        { row: 11, col: 1, seatNum: 5 }, { row: 11, col: 2, seatNum: 6 },
        { row: 11, col: 3, seatNum: null }, // Aisle (skip)
        { row: 11, col: 4, seatNum: 12 }, { row: 11, col: 5, seatNum: 11 },
        // Row 12
        { row: 12, col: 1, seatNum: 1 }, { row: 12, col: 2, seatNum: 2 },
        { row: 12, col: 3, seatNum: null }, // Aisle (skip)
        { row: 12, col: 4, seatNum: 8 }, { row: 12, col: 5, seatNum: 7 },
        // Row 13
        { row: 13, col: 1, seatNum: null }, { row: 13, col: 2, seatNum: null },
        { row: 13, col: 3, seatNum: null }, // Aisle (skip)
        { row: 13, col: 4, seatNum: 4 }, { row: 13, col: 5, seatNum: 3 },
      ];
      
      // Process only actual seats (skip null/empty cells)
      seatPositions.forEach(({ row, col, seatNum }) => {
        if (seatNum !== null) {
          const seatNumberStr = seatNum.toString().padStart(2, '0');
          const isBooked = bookedSeats.some(bs => {
            if (!bs || !bs.seatNumber) return false;
            const bsSeatNumber = bs.seatNumber.toString().trim();
            return bsSeatNumber === seatNumberStr || 
                   bsSeatNumber === seatNum.toString() || 
                   parseInt(bsSeatNumber) === seatNum;
          });
          
          const bookedSeat = bookedSeats.find(bs => {
            if (!bs || !bs.seatNumber) return false;
            const bsSeatNumber = bs.seatNumber.toString().trim();
            return bsSeatNumber === seatNumberStr || 
                   bsSeatNumber === seatNum.toString() || 
                   parseInt(bsSeatNumber) === seatNum;
          });
          
          const isNotAvailable = notAvailableSeatNumbers.includes(seatNum);
          
          let isBookedByGents = false;
          let isBookedByLadies = false;
          
          if (isBooked && bookedSeat) {
            const genderValue = bookedSeat.gender || bookedSeat.Gender || bookedSeat.genderValue;
            if (genderValue) {
              const genderLower = genderValue.toString().toLowerCase().trim();
              isBookedByGents = genderLower === 'male' || genderLower === 'm' || genderLower === 'gent' || genderLower === 'gents' || genderLower === 'man' || genderLower === 'men';
              isBookedByLadies = genderLower === 'female' || genderLower === 'f' || genderLower === 'lady' || genderLower === 'ladies' || genderLower === 'woman' || genderLower === 'women';
            }
          }
          
          const paymentStatus = bookedSeat?.paymentStatus || bookedSeat?.PaymentStatus || bookedSeat?.paymentstatus || null;
          const paymentStatusLower = paymentStatus ? paymentStatus.toString().toLowerCase().trim() : '';
          const isPaymentCompleted = paymentStatusLower === 'completed' || paymentStatusLower === '1' || paymentStatus === 1 || paymentStatus === '1';
          
          seats.push({
            id: seatNumberStr,
            number: seatNumberStr,
            row: row,
            col: col,
            isAvailable: !isBooked && !isNotAvailable,
            isNotAvailable: isNotAvailable,
            isBookedByGents: isBookedByGents,
            isBookedByLadies: isBookedByLadies,
            paymentStatus: paymentStatus,
            isPaymentCompleted: isPaymentCompleted
          });
        }
      });
      
      return seats;
    }
    
    // Standard layout parsing
    let columns = 4;
    let rows = 12;
    
    if (seatLayout) {
      const layoutMatch = seatLayout.toString().match(/(\d+)[x×](\d+)/i);
      if (layoutMatch) {
        columns = parseInt(layoutMatch[1]);
        rows = parseInt(layoutMatch[2]);
      }
    }
    
    let seatNumber = 1;
    
    for (let row = 1; row <= rows; row++) {
      for (let col = 1; col <= columns; col++) {
        const seatNumberStr = seatNumber.toString();
        const isBooked = bookedSeats.some(bs => {
          if (!bs || !bs.seatNumber) return false;
          const bsSeatNumber = bs.seatNumber.toString().trim();
          return bsSeatNumber === seatNumberStr || 
                 bsSeatNumber === seatNumber.toString() || 
                 parseInt(bsSeatNumber) === seatNumber;
        });
        
        const bookedSeat = bookedSeats.find(bs => {
          if (!bs || !bs.seatNumber) return false;
          const bsSeatNumber = bs.seatNumber.toString().trim();
          return bsSeatNumber === seatNumberStr || 
                 bsSeatNumber === seatNumber.toString() || 
                 parseInt(bsSeatNumber) === seatNumber;
        });
        
        const isNotAvailable = notAvailableSeatNumbers.includes(seatNumber);
        
        let isBookedByGents = false;
        let isBookedByLadies = false;
        
        if (isBooked && bookedSeat) {
          // Get gender value - check multiple possible field names
          const genderValue = bookedSeat.gender || bookedSeat.Gender || bookedSeat.genderValue;
          
          if (genderValue) {
            const genderLower = genderValue.toString().toLowerCase().trim();
            // Check for male variations
            isBookedByGents = genderLower === 'male' || 
                             genderLower === 'm' || 
                             genderLower === 'gent' || 
                             genderLower === 'gents' ||
                             genderLower === 'man' ||
                             genderLower === 'men';
            // Check for female variations
            isBookedByLadies = genderLower === 'female' || 
                              genderLower === 'f' || 
                              genderLower === 'lady' || 
                              genderLower === 'ladies' ||
                              genderLower === 'woman' ||
                              genderLower === 'women';
            
            // Debug: Log gender detection for this seat
            if (seatNumber <= 5) { // Only log first 5 seats to avoid spam
              console.log(`Seat ${seatNumber}: gender="${genderValue}" (lower="${genderLower}") -> isBookedByGents=${isBookedByGents}, isBookedByLadies=${isBookedByLadies}`);
            }
          }
        }
        
        // Get payment status from booked seat
        const paymentStatus = bookedSeat?.paymentStatus || bookedSeat?.PaymentStatus || bookedSeat?.paymentstatus || null;
        // Check for various payment status formats: "Completed", "completed", "COMPLETED", 1, "1", etc.
        const paymentStatusLower = paymentStatus ? paymentStatus.toString().toLowerCase().trim() : '';
        const isPaymentCompleted = paymentStatusLower === 'completed' || paymentStatusLower === '1' || paymentStatus === 1 || paymentStatus === '1';
        
        // Debug: Log payment status for first few seats
        if (seatNumber <= 5 && isBooked) {
          console.log(`Seat ${seatNumber}: paymentStatus="${paymentStatus}" (type: ${typeof paymentStatus}), paymentStatusLower="${paymentStatusLower}", isPaymentCompleted=${isPaymentCompleted}`);
        }
        
        seats.push({
          id: seatNumberStr,
          number: seatNumberStr,
          row: row,
          col: col,
          isAvailable: !isBooked && !isNotAvailable,
          isNotAvailable: isNotAvailable,
          isBookedByGents: isBookedByGents,
          isBookedByLadies: isBookedByLadies,
          paymentStatus: paymentStatus,
          isPaymentCompleted: isPaymentCompleted
        });
        
        seatNumber++;
      }
    }
    return seats;
  };

  // Get seat class for styling
  const getSeatClass = (seat) => {
    if (seat.isNotAvailable) {
      return 'bg-gray-400 text-white cursor-not-allowed';
    }
    if (seat.isBookedByGents) {
      return 'bg-red-500 text-white cursor-not-allowed';
    }
    if (seat.isBookedByLadies) {
      return 'bg-pink-500 text-white cursor-not-allowed';
    }
    if (seat.isAvailable) {
      return 'bg-green-500 text-white cursor-pointer';
    }
    return 'bg-gray-300 text-gray-600 cursor-not-allowed';
  };

  // Handle seat click to show booking details
  const handleSeatClick = async (seat) => {
    // Only show details for booked seats
    if (seat.isAvailable || seat.isNotAvailable) {
      return;
    }

    setSelectedSeatForDetails(seat);
    setBookingDetailsLoading(true);
    setBookingDetails(null);
    setBookingSeats([]);
    setEditingSeats(false);
    setEditedSeatNumbers({});

    try {
      // Find the bookingID from bookedSeatsData
      const seatData = bookedSeatsData.find(s => s.seatNumber === seat.number);
      
      if (seatData && seatData.bookingID) {
        const bookingId = seatData.bookingID;
        
        // Load booking details
        const booking = await bookingApi.getBooking(bookingId);
        
        // The API returns the booking data directly (not wrapped)
        // Handle different possible response structures
        let bookingData = booking;
        if (booking && typeof booking === 'object') {
          // If response has value property, use it
          if (booking.value && (booking.value.bookingID || booking.value.BookingID)) {
            bookingData = booking.value;
          }
          // If response has data property, use it
          else if (booking.data && (booking.data.bookingID || booking.data.BookingID)) {
            bookingData = booking.data;
          }
          // Otherwise use the booking directly if it has bookingID
          else if (booking.bookingID || booking.BookingID) {
            bookingData = booking;
          }
        }
        
        if (bookingData && (bookingData.bookingID || bookingData.BookingID)) {
          setBookingDetails(bookingData);
          
          // Load all booking seats for this booking
          try {
            const seatsResponse = await bookingApi.getBookingSeats(bookingId);
            let seatsData = seatsResponse;
            
            if (seatsResponse?.value && Array.isArray(seatsResponse.value)) {
              seatsData = seatsResponse.value;
            } else if (seatsResponse?.data && Array.isArray(seatsResponse.data)) {
              seatsData = seatsResponse.data;
            } else if (Array.isArray(seatsResponse)) {
              seatsData = seatsResponse;
            }
            
            if (Array.isArray(seatsData)) {
              setBookingSeats(seatsData);
              // Initialize edited seat numbers with current values
              const initialEdited = {};
              seatsData.forEach(seat => {
                const seatId = seat.bookingSeatID || seat.BookingSeatID || seat.id;
                const seatNum = seat.seatNumber || seat.SeatNumber || '';
                if (seatId) {
                  initialEdited[seatId] = seatNum.toString();
                }
              });
              setEditedSeatNumbers(initialEdited);
            }
          } catch (seatError) {
            console.error('Error loading booking seats:', seatError);
            // Don't show error if seats can't be loaded, just continue
          }
        } else {
          throw new Error('Invalid booking data received from server');
        }
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Booking Found',
          text: 'No booking details available for this seat.'
        });
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to load booking details: ${error.message || 'Unknown error'}`
      });
    } finally {
      setBookingDetailsLoading(false);
    }
  };

  // Handle updating seat number
  const handleUpdateSeatNumber = async (bookingSeatId, oldSeatNumber, newSeatNumber) => {
    if (!newSeatNumber || newSeatNumber.toString().trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Seat Number',
        text: 'Please enter a valid seat number.',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    const bookingId = bookingDetails?.bookingID || bookingDetails?.BookingID;
    if (!bookingId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Booking ID not found.',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    // Check if new seat is available (not booked by another booking for the same schedule date)
    const newSeatNumStr = newSeatNumber.toString().trim();
    const isSeatAvailable = seats.find(s => s.number === newSeatNumStr || s.number === newSeatNumStr.padStart(2, '0'))?.isAvailable;
    
    if (!isSeatAvailable && newSeatNumStr !== oldSeatNumber.toString().trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Seat Not Available',
        text: `Seat ${newSeatNumber} is already booked or not available. Please choose another seat.`,
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    try {
      setUpdatingSeat(true);
      
      // Update the booking seat - only send seat number, backend will handle finding/creating seat and preserve price
      const updateData = {
        SeatNumber: newSeatNumStr
      };

      await bookingApi.updateBookingSeat(bookingId, bookingSeatId, updateData);

      // Reload booking seats to get updated data
      const updatedBookingSeatsResponse = await bookingApi.getBookingSeats(bookingId);
      const updatedBookingSeats = updatedBookingSeatsResponse?.value || updatedBookingSeatsResponse?.data || updatedBookingSeatsResponse || [];
      setBookingSeats(updatedBookingSeats);

      // Reload seat layout to reflect changes
      if (selectedScheduleDateId) {
        // Reload the seat layout by triggering the useEffect
        // Force a reload by clearing and resetting
        const currentScheduleDateId = selectedScheduleDateId;
        setSelectedScheduleDateId('');
        setTimeout(() => {
          setSelectedScheduleDateId(currentScheduleDateId);
        }, 100);
      }

      // Track last updated seat for SMS sending
      setLastUpdatedSeat({
        bookingSeatId,
        oldSeatNumber,
        newSeatNumber: newSeatNumStr
      });

      Swal.fire({
        icon: 'success',
        title: 'Seat Updated',
        text: `Seat number changed from ${oldSeatNumber} to ${newSeatNumber} successfully.`,
        confirmButtonColor: '#2563eb'
      });
    } catch (error) {
      console.error('Error updating seat number:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to update seat number: ${error.message || 'Unknown error'}`,
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setUpdatingSeat(false);
    }
  };

  // Send seat change SMS
  const handleSendSeatChangeSms = async (bookingSeatId, oldSeatNumber, newSeatNumber) => {
    const bookingId = bookingDetails?.bookingID || bookingDetails?.BookingID;
    if (!bookingId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Booking ID not found.',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    try {
      setSendingSms(true);
      
      await bookingApi.sendSeatChangeSms(bookingId, bookingSeatId, {
        OldSeatNumber: oldSeatNumber.toString().trim(),
        NewSeatNumber: newSeatNumber.toString().trim()
      });

      // Clear last updated seat info after sending SMS
      setLastUpdatedSeat(null);

      Swal.fire({
        icon: 'success',
        title: 'SMS Sent',
        text: `SMS notification sent to customer about seat change from ${oldSeatNumber} to ${newSeatNumber}.`,
        confirmButtonColor: '#2563eb'
      });
    } catch (error) {
      console.error('Error sending seat change SMS:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to send SMS: ${error.message || 'Unknown error'}`,
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setSendingSms(false);
    }
  };

  // Open cancel booking modal
  const handleOpenCancelModal = (bookingId) => {
    setCancelBookingId(bookingId);
    setCancelRemark('');
    setShowCancelModal(true);
  };

  // Cancel booking
  const handleCancelBooking = async () => {
    if (!cancelBookingId) return;

    try {
      setCancellingBooking(true);

      await bookingApi.adminCancelBooking(cancelBookingId, cancelRemark || '');

      // Close modal
      setShowCancelModal(false);
      setCancelBookingId(null);
      setCancelRemark('');

      // Refresh bookings
      await handleSearchBookings();

      Swal.fire({
        icon: 'success',
        title: 'Booking Cancelled',
        text: 'Booking has been cancelled successfully. SMS notification sent to customer.',
        confirmButtonColor: '#2563eb'
      });
    } catch (error) {
      console.error('Error cancelling booking:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to cancel booking: ${error.message || 'Unknown error'}`,
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setCancellingBooking(false);
    }
  };

  // View and download receipt (opens in new window with download option)
  const handleViewReceipt = async (bookingId) => {
    try {
      Swal.fire({
        title: 'Loading Receipt...',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const receiptBlob = await bookingApi.getBookingReceipt(bookingId);
      const bookingRef = `BK${bookingId.toString().padStart(6, '0')}`;
      let url;
      
      // Check if it's HTML (for PDF generation via print)
      if (receiptBlob.type === 'text/html' || receiptBlob.type === 'text/html; charset=utf-8') {
        // Read HTML content and update title tag
        const htmlText = await receiptBlob.text();
        let updatedHtml = htmlText.replace(
          /<title>.*?<\/title>/i,
          `<title>Booking Receipt_${bookingRef}</title>`
        );
        // If no title tag exists, add one in the head section
        if (!updatedHtml.includes('<title>')) {
          updatedHtml = updatedHtml.replace(
            /<head>/i,
            `<head><title>Booking Receipt_${bookingRef}</title>`
          );
        }
        const updatedBlob = new Blob([updatedHtml], { type: 'text/html' });
        url = window.URL.createObjectURL(updatedBlob);
        
        // Open in new window and trigger print to PDF
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.onload = () => {
            setTimeout(() => {
              Swal.close();
              Swal.fire({
                icon: 'success',
                title: 'Receipt Ready',
                html: `
                  <p style="margin-bottom: 15px;">Receipt opened in new window.</p>
                  <p style="margin-bottom: 10px; color: #666; font-size: 14px;">You can:</p>
                  <ul style="text-align: left; color: #666; font-size: 14px; margin: 10px 0;">
                    <li>Press <strong>Ctrl+P</strong> (Windows) or <strong>Cmd+P</strong> (Mac) to print</li>
                    <li>Select "Save as PDF" in the print dialog to download</li>
                    <li>Or close this message and use the print button in the receipt window</li>
                  </ul>
                `,
                confirmButtonText: 'Got it',
                confirmButtonColor: '#2563eb'
              });
            }, 500);
          };
        } else {
          // Fallback: create download link
          const a = document.createElement('a');
          a.href = url;
          a.download = `Booking Receipt_${bookingRef}.html`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          Swal.close();
          Swal.fire({
            icon: 'success',
            title: 'Receipt Downloaded',
            text: 'Your receipt has been downloaded. Open it and use Print to PDF.',
            confirmButtonColor: '#2563eb'
          });
        }
      } else {
        // Direct PDF download
        url = window.URL.createObjectURL(receiptBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Booking Receipt_${bookingRef}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Receipt Downloaded',
          text: 'Your PDF receipt has been downloaded successfully',
          confirmButtonColor: '#2563eb'
        });
      }
    } catch (error) {
      console.error('Error viewing receipt:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to load receipt. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  // Stats data - bookings and revenue from database
  const stats = [
    { 
      title: 'Today\'s Bookings', 
      value: statsLoading ? '...' : todayBookings.toString(), 
      icon: Users, 
      color: 'purple', 
      change: '' 
    },
    { 
      title: 'Revenue Today', 
      value: statsLoading ? '...' : `LKR ${formatRevenue(todayRevenue)}`, 
      icon: DollarSign, 
      color: 'yellow', 
      change: '' 
    },
    { 
      title: 'Active Vehicles', 
      value: vehiclesLoading ? '...' : activeVehicles.toString(), 
      icon: Building2, 
      color: 'blue', 
      change: '' 
    }
  ];


  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  {stat.change && (
                    <p className={`text-sm font-medium text-${stat.color}-600`}>
                      {stat.change} from yesterday
                    </p>
                  )}
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-100 rounded-xl flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Recent Bookings</h3>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-4">
          {/* Date Range, Booking No, and Payment Status Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Booking No */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Booking No
              </label>
              <input
                type="text"
                value={filterBookingNo}
                onChange={(e) => setFilterBookingNo(e.target.value)}
                placeholder="Enter Booking No"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* From Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                From Date (Booking Date)
              </label>
              <input
                type="date"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Filters by booking creation date</p>
            </div>

            {/* To Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                To Date (Booking Date)
              </label>
              <input
                type="date"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
                min={filterFromDate || undefined}
                placeholder="Leave empty to search from From Date only"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Leave empty to search from From Date onwards</p>
            </div>

            {/* Travel From Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Travel From Date
              </label>
              <input
                type="date"
                value={filterTravelFromDate}
                onChange={(e) => setFilterTravelFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Travel To Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Travel To Date
              </label>
              <input
                type="date"
                value={filterTravelToDate}
                onChange={(e) => setFilterTravelToDate(e.target.value)}
                min={filterTravelFromDate || undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Filters by travel date</p>
            </div>

            {/* Route Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Route
              </label>
              <select
                value={filterRouteId}
                onChange={(e) => setFilterRouteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Routes</option>
                {filterRoutes.map((route) => (
                  <option key={route.routeID || route.RouteID} value={route.routeID || route.RouteID}>
                    {route.fromLocation || route.FromLocation} → {route.toLocation || route.ToLocation}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Status
              </label>
              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 items-end">
              <button
                onClick={handleSearchBookings}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </button>
              <button
                onClick={handleClearFilters}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear
              </button>
            </div>
          </div>

          {/* Global Search - Search across all columns */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="w-4 h-4 inline mr-1" />
              Search All Columns
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by booking no, passenger name, phone, bus number, route, status..."
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Search across all columns in real-time</p>
          </div>
        </div>

        {/* Bookings Table - Responsive with Expandable Rows */}
        {bookingsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading bookings...</span>
          </div>
        ) : (
          <div className="overflow-x-auto shadow-lg rounded-lg border border-gray-200 bg-white">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Booking No</th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Travel Date</span>
                    </div>
                  </th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Booking Date</span>
                    </div>
                  </th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>Route</span>
                    </div>
                  </th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Customer Name</th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Contact Number</th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Amount</th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Payment Status</th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Gateway Status</th>
                  <th className="text-left py-3 px-3 sm:px-4 font-semibold text-gray-700 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const searchFilteredBookings = getFilteredBookingsBySearch();
                  return searchFilteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-12 text-gray-500">
                        <div className="flex flex-col items-center">
                          <Calendar className="w-12 h-12 text-gray-300 mb-2" />
                          <p className="text-lg font-medium">No bookings found</p>
                          <p className="text-sm text-gray-400 mt-1">
                            {searchTerm ? 'Try adjusting your search term' : 'Try adjusting your filters'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    searchFilteredBookings.map((booking) => {
                    const bookingId = booking.bookingID || booking.BookingID || booking.id;
                    const travelDate = booking.travelDate || booking.TravelDate;
                    const bookingDate = booking.bookingDate || booking.BookingDate;
                    const phone = booking.customerPhone || booking.CustomerPhone || booking.phone || booking.Customer?.Phone || 'N/A';
                    const passengerName = booking.customer?.fullName || 
                                        booking.Customer?.FullName ||
                                        booking.customerName ||
                                        booking.CustomerName ||
                                        booking.Customer?.CustomerName ||
                                        'N/A';
                    const createdByUser = booking.user?.fullName || 
                                         booking.User?.FullName ||
                                         booking.user?.FullName ||
                                         booking.User?.fullName ||
                                         booking.createdByName ||
                                         booking.CreatedByName ||
                                         'N/A';
                    const createdBy = createdByUser !== 'N/A' ? createdByUser : 'N/A';
                    const totalAmount = booking.totalAmount || booking.TotalAmount || 0;
                    const bookingStatus = (booking.status || booking.Status || 'Confirmed').toString();
                    const paymentStatus = (booking.paymentStatus || booking.PaymentStatus || 'Pending').toString();
                    const specialRequests = booking.specialRequests || booking.SpecialRequests || null;
                    
                    // Extract seat numbers from BookingSeats
                    const seatNumbers = [];
                    if (booking.BookingSeats && Array.isArray(booking.BookingSeats)) {
                      booking.BookingSeats.forEach(bs => {
                        const seatNum = bs.seatNumber || bs.SeatNumber;
                        if (seatNum) {
                          seatNumbers.push(seatNum.toString());
                        }
                      });
                    } else if (booking.bookingSeats && Array.isArray(booking.bookingSeats)) {
                      booking.bookingSeats.forEach(bs => {
                        const seatNum = bs.seatNumber || bs.SeatNumber;
                        if (seatNum) {
                          seatNumbers.push(seatNum.toString());
                        }
                      });
                    } else if (booking.Seat?.SeatNumber) {
                      seatNumbers.push(booking.Seat.SeatNumber.toString());
                    } else if (booking.seat?.seatNumber) {
                      seatNumbers.push(booking.seat.seatNumber.toString());
                    }
                    const seatNumbersText = seatNumbers.length > 0 ? seatNumbers.sort((a, b) => parseInt(a) - parseInt(b)).join(', ') : 'N/A';
                    
                    // Extract route information
                    const fromLocation = booking.schedule?.route?.fromLocation || 
                                       booking.Schedule?.Route?.FromLocation ||
                                       booking.schedule?.route?.fromCityName ||
                                       booking.Schedule?.Route?.FromCityName ||
                                       'N/A';
                    const toLocation = booking.schedule?.route?.toLocation || 
                                     booking.Schedule?.Route?.ToLocation ||
                                     booking.schedule?.route?.toCityName ||
                                     booking.Schedule?.Route?.ToCityName ||
                                     'N/A';
                    
                    // Extract bus number
                    const busNumber = booking.schedule?.vehicle?.registrationNumber || 
                                    booking.Schedule?.Vehicle?.RegistrationNumber ||
                                    booking.schedule?.vehicle?.registrationNo ||
                                    booking.Schedule?.Vehicle?.RegistrationNo ||
                                    'N/A';
                    
                    // Extract departure and arrival times
                    const departureTime = booking.scheduleDate?.departureTime || 
                                         booking.ScheduleDate?.DepartureTime ||
                                         booking.scheduleDate?.departureTime ||
                                         booking.ScheduleDate?.DepartureTime ||
                                         'N/A';
                    const arrivalTime = booking.scheduleDate?.arrivalTime || 
                                       booking.ScheduleDate?.ArrivalTime ||
                                       booking.scheduleDate?.arrivalTime ||
                                       booking.ScheduleDate?.ArrivalTime ||
                                       'N/A';
                    
                    const isExpanded = expandedRows.has(bookingId);
                    
                    const toggleRow = () => {
                      const newExpanded = new Set(expandedRows);
                      if (isExpanded) {
                        newExpanded.delete(bookingId);
                      } else {
                        newExpanded.add(bookingId);
                      }
                      setExpandedRows(newExpanded);
                    };
                    
                    return (
                      <React.Fragment key={bookingId}>
                        <tr 
                          className="border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer"
                          onClick={toggleRow}
                        >
                          <td className="py-3 px-3 sm:px-4">
                            <div className="flex items-center space-x-2">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="font-bold text-blue-600">#{bookingId}</span>
                              {specialRequests && (
                                <AlertCircle 
                                  className="w-4 h-4 text-yellow-600" 
                                  title={`Special Request: ${specialRequests}`}
                                />
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <div className="flex items-center space-x-1 text-sm">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{formatDate(travelDate)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <div className="flex items-center space-x-1 text-sm">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{formatDate(bookingDate)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <div className="flex items-center space-x-1 text-sm">
                              <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <div className="flex items-center space-x-1 min-w-0">
                                <span className="font-medium text-gray-900 truncate">{fromLocation}</span>
                                <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className="font-medium text-gray-900 truncate">{toLocation}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <p className="text-sm font-medium text-gray-900">{passengerName}</p>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <p className="text-sm text-gray-900">{phone}</p>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <span className="font-bold text-green-600 text-sm sm:text-base">
                              {formatRevenue(totalAmount)}
                            </span>
                          </td>
                          <td className="py-3 px-3 sm:px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              paymentStatus.toLowerCase() === 'paid' || paymentStatus.toLowerCase() === 'completed'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : paymentStatus.toLowerCase() === 'failed'
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                            }`}>
                              {paymentStatus}
                            </span>
                          </td>
                          <td className="py-3 px-3 sm:px-4" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const gatewayStatus = gatewayStatuses[bookingId];
                              if (gatewayStatus?.checking) {
                                return (
                                  <div className="flex items-center space-x-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                    <span className="text-xs text-gray-500">Checking...</span>
                                  </div>
                                );
                              }
                              if (gatewayStatus?.error) {
                                return (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setGatewayStatuses(prev => ({
                                        ...prev,
                                        [bookingId]: { checking: true, status: null, error: null }
                                      }));
                                      try {
                                        const response = await paymentApi.checkPaymentStatus(bookingId);
                                        const data = response?.value || response?.data || response;
                                        setGatewayStatuses(prev => ({
                                          ...prev,
                                          [bookingId]: { 
                                            checking: false, 
                                            status: data.isPaymentSuccessful ? 'Success' : 'Failed',
                                            error: null,
                                            details: data
                                          }
                                        }));
                                      } catch (error) {
                                        setGatewayStatuses(prev => ({
                                          ...prev,
                                          [bookingId]: { 
                                            checking: false, 
                                            status: null, 
                                            error: error.response?.data?.message || error.message || 'Failed to check'
                                          }
                                        }));
                                      }
                                    }}
                                    className="text-xs text-red-600 hover:text-red-700 underline"
                                    title="Click to retry"
                                  >
                                    Error (Retry)
                                  </button>
                                );
                              }
                              if (gatewayStatus?.status) {
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedGatewayStatus({
                                        bookingId: bookingId,
                                        bookingRef: `BK${String(bookingId).padStart(6, '0')}`,
                                        customerName: passengerName,
                                        ...gatewayStatus
                                      });
                                      setShowGatewayStatusModal(true);
                                    }}
                                    className={`px-2 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity ${
                                      gatewayStatus.status === 'Success'
                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                        : 'bg-red-100 text-red-800 border border-red-200'
                                    }`}
                                    title="Click to view details"
                                  >
                                    {gatewayStatus.status}
                                  </button>
                                );
                              }
                              // No status checked yet - show "Not Checked" or start checking automatically
                              // This should rarely happen as we auto-check on load, but handle it gracefully
                              return (
                                <span className="text-xs text-gray-400">-</span>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-3 sm:px-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewReceipt(bookingId)}
                                className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-xs sm:text-sm font-medium shadow-sm hover:shadow-md"
                              >
                                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                <span className="hidden sm:inline">Download</span>
                              </button>
                              {/* Cancel button - only for upcoming bookings that are not already cancelled */}
                              {(() => {
                                const travelDateObj = travelDate ? new Date(travelDate) : null;
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const isUpcoming = travelDateObj && travelDateObj >= today;
                                const isCancelled = bookingStatus.toLowerCase() === 'cancelled';
                                
                                return isUpcoming && !isCancelled ? (
                                  <button
                                    onClick={() => handleOpenCancelModal(bookingId)}
                                    className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-xs sm:text-sm font-medium shadow-sm hover:shadow-md"
                                    title="Cancel Booking"
                                  >
                                    <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                    <span className="hidden sm:inline">Cancel</span>
                                  </button>
                                ) : null;
                              })()}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <td colSpan="10" className="py-4 px-4 sm:px-6">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                                  <p className="text-sm font-medium text-gray-900">{phone}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Passenger</p>
                                  <p className="text-sm font-medium text-gray-900">{passengerName}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Created By</p>
                                  <p className="text-sm font-medium text-gray-900">{createdBy}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Bus Number</p>
                                  <div className="flex items-center space-x-1">
                                    <Bus className="w-4 h-4 text-green-600" />
                                    <p className="text-sm font-medium text-gray-900">{busNumber}</p>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Time</p>
                                  <div className="flex flex-col space-y-1 text-sm">
                                    <div className="flex items-center space-x-1">
                                      <Clock className="w-3 h-3 text-blue-600" />
                                      <span className="text-gray-700">{formatTime(departureTime)}</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Clock className="w-3 h-3 text-green-600" />
                                      <span className="text-gray-700">{formatTime(arrivalTime)}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Booking Status</p>
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    bookingStatus.toLowerCase() === 'confirmed'
                                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                      : bookingStatus.toLowerCase() === 'completed'
                                      ? 'bg-green-100 text-green-800 border border-green-200'
                                      : bookingStatus.toLowerCase() === 'cancelled'
                                      ? 'bg-red-100 text-red-800 border border-red-200'
                                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                                  }`}>
                                    {bookingStatus}
                                  </span>
                                </div>
                                {(() => {
                                  const gatewayStatus = gatewayStatuses[bookingId];
                                  if (gatewayStatus?.details) {
                                    return (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Gateway Status</p>
                                        <div className="space-y-1">
                                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                            gatewayStatus.status === 'Success'
                                              ? 'bg-green-100 text-green-800 border border-green-200'
                                              : 'bg-red-100 text-red-800 border border-red-200'
                                          }`}>
                                            {gatewayStatus.status}
                                          </span>
                                          {gatewayStatus.details.paymentStatus && (
                                            <p className="text-xs text-gray-600">
                                              Status: {gatewayStatus.details.paymentStatus}
                                            </p>
                                          )}
                                          {gatewayStatus.details.result && (
                                            <p className="text-xs text-gray-600">
                                              Result: {gatewayStatus.details.result}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Seat Numbers</p>
                                  <div className="flex flex-wrap gap-1">
                                    {seatNumbers.length > 0 ? (
                                      seatNumbers.map((seatNum, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                                        >
                                          {seatNum}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-sm text-gray-500">N/A</span>
                                    )}
                                  </div>
                                </div>
                                {specialRequests && (
                                  <div className="sm:col-span-2 lg:col-span-3">
                                    <p className="text-xs text-gray-500 mb-1">Special Requests</p>
                                    <p className="text-sm font-medium text-gray-900 bg-yellow-50 p-2 rounded border border-yellow-200">
                                      {specialRequests}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                  );
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );


  return (
    <div className="w-screen min-w-screen max-w-screen min-h-screen overflow-x-hidden bg-gray-50">
      {/* Header - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">
              Manager Dashboard
            </h1>
            <p className="text-xs sm:text-sm text-blue-100">
              Manage your transport operations efficiently
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Overview Content */}
          <div className="min-h-96">
            {renderOverview()}
          </div>

          {/* Seat Availability Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Seat Availability</h3>
              {selectedScheduleDateId && selectedRouteId && (
                <button
                  onClick={downloadSeatAvailabilityPDF}
                  disabled={!selectedScheduleDateId || !selectedRouteId}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title={!selectedScheduleDateId || !selectedRouteId ? 'Please select a route and date to download PDF' : 'Download Seat Availability Report'}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </button>
              )}
            </div>
            
            {/* Date and Route Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Select Date
                </label>
                <input
                  type="date"
                  value={selectedViewDate}
                  onChange={(e) => {
                    setSelectedViewDate(e.target.value);
                    // Clear routes and selections when date changes
                    setAvailableRoutes([]);
                    setSelectedRouteId('');
                    setSelectedScheduleId('');
                    setSelectedScheduleDateId('');
                    setSelectedRouteInfo(null);
                    setSeats([]);
                    setScheduleDateDetails(null);
                  }}
                  min={getMinSelectableDate()}
                  max={getMaxSelectableDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Select Route
                </label>
                <select
                  value={selectedRouteId}
                  onChange={(e) => {
                    const selected = availableRoutes.find(r => 
                      `${r.routeId}_${r.scheduleId}` === e.target.value
                    );
                    if (selected) {
                      setSelectedRouteId(e.target.value);
                      setSelectedScheduleId(selected.scheduleId);
                      setSelectedScheduleDateId(selected.scheduleDateId);
                      setSelectedRouteInfo(selected); // Store route info for display
                    }
                  }}
                  disabled={routesLoading || availableRoutes.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">
                    {routesLoading ? 'Loading routes...' : availableRoutes.length === 0 ? 'Click Search to load routes' : 'Select a route'}
                  </option>
                  {availableRoutes.map((route, index) => (
                    <option key={index} value={`${route.routeId}_${route.scheduleId}`}>
                      {route.routeName} - {route.scheduleName} ({route.departureTime})
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {/* Summary Statistics */}
            {selectedScheduleDateId && scheduleDateDetails && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{summaryStats.totalSeats}</div>
                  <div className="text-sm text-gray-600">Total Seats</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{summaryStats.availableSeats}</div>
                  <div className="text-sm text-gray-600">Available</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{summaryStats.bookedSeats}</div>
                  <div className="text-sm text-gray-600">Booked</div>
                </div>
                <div className="bg-gray-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-600">{summaryStats.notAvailableSeats}</div>
                  <div className="text-sm text-gray-600">Not Available</div>
                </div>
                <div className="bg-green-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{summaryStats.completedPayments}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{summaryStats.pendingPayments}</div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
              </div>
            )}

            {/* Seat Layout */}
            {seatLayoutLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading seat layout...</span>
              </div>
            ) : selectedScheduleDateId && seats.length > 0 ? (
              <div>
                {/* Journey Details */}
                {(scheduleDateDetails || selectedRouteInfo) && (
                  <div className="bg-blue-50 rounded-xl p-4 mb-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Route</p>
                        <p className="font-semibold text-gray-900">
                          {selectedRouteInfo?.routeName || 
                           (scheduleDateDetails?.schedule?.route ? 
                             `${scheduleDateDetails.schedule.route.fromCity?.cityName || scheduleDateDetails.schedule.route.fromCityName || scheduleDateDetails.schedule.route.fromLocation || 'N/A'} → ${scheduleDateDetails.schedule.route.toCity?.cityName || scheduleDateDetails.schedule.route.toCityName || scheduleDateDetails.schedule.route.toLocation || 'N/A'}` :
                             'N/A')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Departure</p>
                        <p className="font-semibold text-gray-900">
                          {selectedRouteInfo?.departureTime || 
                           scheduleDateDetails?.departureTime || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Arrival</p>
                        <p className="font-semibold text-gray-900">
                          {selectedRouteInfo?.arrivalTime || 
                           scheduleDateDetails?.arrivalTime || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Bus Number</p>
                        <p className="font-semibold text-gray-900">
                          {selectedRouteInfo?.busNumber || 
                           scheduleDateDetails?.schedule?.vehicle?.registrationNumber || 
                           scheduleDateDetails?.schedule?.vehicle?.registrationNo ||
                           scheduleDateDetails?.vehicle?.registrationNumber || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Seat Legend */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-sm text-gray-600">Available</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span className="text-sm text-gray-600">Gents</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-pink-500 rounded"></div>
                    <span className="text-sm text-gray-600">Ladies</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-gray-400 rounded"></div>
                    <span className="text-sm text-gray-600">Not Available</span>
                  </div>
                </div>

                {/* Seat Grid */}
                <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
                  <div className="text-center mb-4">
                    <div className="inline-block bg-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">
                      Front - Driver Area
                    </div>
                  </div>

                  {/* Rear Door - At Top (end of seat layout) */}
                  <div className="text-center mb-4">
                    <div className="inline-block bg-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">
                      Rear Door
                    </div>
                  </div>

                  {(() => {
                    const layoutMatch = scheduleDateDetails?.schedule?.vehicle?.seatLayout?.match(/(\d+)[x×](\d+)/i);
                    const columns = layoutMatch ? parseInt(layoutMatch[1]) : 4;
                    const rows = layoutMatch ? parseInt(layoutMatch[2]) : 12;
                    
                    const seatsByColumn = {};
                    seats.forEach(seat => {
                      if (!seatsByColumn[seat.col]) {
                        seatsByColumn[seat.col] = [];
                      }
                      seatsByColumn[seat.col].push(seat);
                    });
                    
                    const sortedColumns = Object.keys(seatsByColumn).sort((a, b) => parseInt(a) - parseInt(b));
                    const leftColumns = sortedColumns.filter(col => parseInt(col) <= 2);
                    const rightColumns = sortedColumns.filter(col => parseInt(col) > 2);
                    
                    return (
                      <div className="flex justify-center items-start gap-4">
                        {/* Left side */}
                        <div className="flex gap-2">
                          {leftColumns.map(colNum => {
                            const columnSeats = seatsByColumn[colNum].sort((a, b) => a.row - b.row);
                            const isFirstColumn = parseInt(colNum) === Math.min(...leftColumns.map(c => parseInt(c)));
                            
                            return (
                              <div key={colNum} className="flex flex-col items-center gap-2">
                                <div className="mb-2 h-6"></div>
                                {columnSeats.map((seat) => {
                                  const isSeat01 = seat.number === '01' || seat.number === '1';
                                  return (
                                    <React.Fragment key={seat.id}>
                                  <div
                                        className={`w-12 h-12 rounded-lg font-medium text-sm flex items-center justify-center relative ${getSeatClass(seat)} ${!seat.isAvailable && !seat.isNotAvailable ? 'cursor-pointer' : ''}`}
                                    title={`Seat ${seat.number} - ${seat.isNotAvailable ? 'Not Available' : seat.isBookedByGents ? `Booked by Gents (${seat.paymentStatus || 'Pending'})` : seat.isBookedByLadies ? `Booked by Ladies (${seat.paymentStatus || 'Pending'})` : 'Available'}`}
                                    onClick={() => handleSeatClick(seat)}
                                  >
                                    {seat.number}
                                    {/* Small horizontal line near bottom inside the box to indicate payment completed */}
                                    {seat.isPaymentCompleted && !seat.isAvailable && !seat.isNotAvailable && (
                                      <div className="absolute bottom-1 left-1 right-1 h-0.5 bg-green-500 rounded"></div>
                                    )}
                                  </div>
                                      {/* Show wheel icon below (after) seat 01 */}
                                      {isSeat01 && isFirstColumn && (
                                        <div className="mt-1">
                                          <img 
                                            src="/images/wheel.svg" 
                                            alt="Steering Wheel" 
                                            className="w-6 h-6"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                              </div>
                                      )}
                                    </React.Fragment>
                            );
                          })}
                        </div>
                            );
                          })}
                        </div>
                        
                        {/* Right side */}
                        <div className="flex gap-2">
                          {rightColumns.map((colNum, index) => {
                            const columnSeats = seatsByColumn[colNum].sort((a, b) => a.row - b.row);
                            
                            return (
                              <div key={colNum} className="flex flex-col items-center gap-2">
                                <div className="mb-2 h-6"></div>
                                
                                {columnSeats.map((seat) => (
                                  <div
                                    key={seat.id}
                                    className={`w-12 h-12 rounded-lg font-medium text-sm flex items-center justify-center relative ${getSeatClass(seat)} ${!seat.isAvailable && !seat.isNotAvailable ? 'cursor-pointer' : ''}`}
                                    title={`Seat ${seat.number} - ${seat.isNotAvailable ? 'Not Available' : seat.isBookedByGents ? `Booked by Gents (${seat.paymentStatus || 'Pending'})` : seat.isBookedByLadies ? `Booked by Ladies (${seat.paymentStatus || 'Pending'})` : 'Available'}`}
                                    onClick={() => handleSeatClick(seat)}
                                  >
                                    {seat.number}
                                    {/* Small horizontal line near bottom inside the box to indicate payment completed */}
                                    {seat.isPaymentCompleted && !seat.isAvailable && !seat.isNotAvailable && (
                                      <div className="absolute bottom-1 left-1 right-1 h-0.5 bg-green-500 rounded"></div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : selectedScheduleDateId ? (
              <div className="text-center py-12 text-gray-500">
                No seat layout available
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Please select a date and route to view seat layout
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Booking Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => {
          if (!cancellingBooking) {
            setShowCancelModal(false);
            setCancelBookingId(null);
            setCancelRemark('');
          }
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Cancel Booking #{cancelBookingId}
              </h3>
              <button
                onClick={() => {
                  if (!cancellingBooking) {
                    setShowCancelModal(false);
                    setCancelBookingId(null);
                    setCancelRemark('');
                  }
                }}
                disabled={cancellingBooking}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Remark <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  value={cancelRemark}
                  onChange={(e) => setCancelRemark(e.target.value)}
                  placeholder="Enter reason for cancellation..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  disabled={cancellingBooking}
                />
                <p className="mt-1 text-xs text-gray-500">
                  This remark will be saved with the booking. SMS will be sent to the customer automatically.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleCancelBooking}
                  disabled={cancellingBooking}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                >
                  {cancellingBooking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Confirm Cancellation
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (!cancellingBooking) {
                      setShowCancelModal(false);
                      setCancelBookingId(null);
                      setCancelRemark('');
                    }
                  }}
                  disabled={cancellingBooking}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Details Modal */}
      {selectedSeatForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => {
          setSelectedSeatForDetails(null);
          setBookingDetails(null);
          setLastUpdatedSeat(null);
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Booking Details - Seat {selectedSeatForDetails.number}
              </h3>
              <button
                onClick={() => {
                  setSelectedSeatForDetails(null);
                  setBookingDetails(null);
                  setLastUpdatedSeat(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {bookingDetailsLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading booking details...</span>
              </div>
            ) : bookingDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Booking No</p>
                    <p className="font-semibold text-gray-900">
                      BK{bookingDetails.bookingID?.toString().padStart(6, '0') || bookingDetails.BookingID?.toString().padStart(6, '0') || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Status</p>
                    <p className={`font-semibold ${
                      (bookingDetails.paymentStatus || bookingDetails.PaymentStatus) === 'Completed' 
                        ? 'text-green-600' 
                        : 'text-yellow-600'
                    }`}>
                      {bookingDetails.paymentStatus || bookingDetails.PaymentStatus || 'Pending'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Booked Date</p>
                    <p className="font-semibold text-gray-900">
                      {bookingDetails.bookingDate || bookingDetails.BookingDate 
                        ? new Date(bookingDetails.bookingDate || bookingDetails.BookingDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Booked Time</p>
                    <p className="font-semibold text-gray-900">
                      {bookingDetails.bookingDate || bookingDetails.BookingDate
                        ? new Date(bookingDetails.bookingDate || bookingDetails.BookingDate).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Booked By</p>
                    <p className="font-semibold text-gray-900">
                      {bookingDetails.customer?.customerName || 
                       bookingDetails.customer?.CustomerName ||
                       bookingDetails.Customer?.CustomerName ||
                       bookingDetails.Customer?.customerName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone Number</p>
                    <p className="font-semibold text-gray-900">
                      {bookingDetails.customer?.phone || 
                       bookingDetails.customer?.Phone ||
                       bookingDetails.Customer?.Phone ||
                       bookingDetails.Customer?.phone || 'N/A'}
                    </p>
                  </div>
                  {(bookingDetails.customer?.email || 
                    bookingDetails.customer?.Email ||
                    bookingDetails.Customer?.Email ||
                    bookingDetails.Customer?.email) && (
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-gray-900">
                        {bookingDetails.customer?.email || 
                         bookingDetails.customer?.Email ||
                         bookingDetails.Customer?.Email ||
                         bookingDetails.Customer?.email}
                      </p>
                    </div>
                  )}
                  {(bookingDetails.customer?.nic || 
                    bookingDetails.customer?.NIC ||
                    bookingDetails.Customer?.NIC ||
                    bookingDetails.Customer?.nic) && (
                    <div>
                      <p className="text-sm text-gray-600">NIC</p>
                      <p className="font-semibold text-gray-900">
                        {bookingDetails.customer?.nic || 
                         bookingDetails.customer?.NIC ||
                         bookingDetails.Customer?.NIC ||
                         bookingDetails.Customer?.nic}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Seat Numbers Section - Editable */}
                {bookingSeats.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Ticket className="w-5 h-5 text-blue-600" />
                        <h4 className="text-lg font-semibold text-gray-900">Seat Numbers</h4>
                      </div>
                      {!editingSeats && (
                        <button
                          onClick={() => setEditingSeats(true)}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      )}
                    </div>
                    
                    {editingSeats ? (
                      <div className="space-y-3">
                        {bookingSeats.map((bookingSeat) => {
                          const seatId = bookingSeat.bookingSeatID || bookingSeat.BookingSeatID || bookingSeat.id;
                          const currentSeatNumber = bookingSeat.seatNumber || bookingSeat.SeatNumber || '';
                          const editedSeatNumber = editedSeatNumbers[seatId] || currentSeatNumber;
                          
                          return (
                            <div key={seatId} className="flex items-center space-x-2">
                              <label className="text-sm font-medium text-gray-700 w-20">
                                Seat:
                              </label>
                              <input
                                type="text"
                                value={editedSeatNumber}
                                onChange={(e) => {
                                  const newValue = e.target.value.replace(/[^0-9]/g, '');
                                  setEditedSeatNumbers(prev => ({
                                    ...prev,
                                    [seatId]: newValue
                                  }));
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter seat number"
                              />
                              <button
                                onClick={() => handleUpdateSeatNumber(seatId, currentSeatNumber, editedSeatNumber)}
                                disabled={updatingSeat || editedSeatNumber === currentSeatNumber || !editedSeatNumber}
                                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-1"
                              >
                                {updatingSeat ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Save className="w-4 h-4" />
                                )}
                                <span>Save</span>
                              </button>
                            </div>
                          );
                        })}
                        <button
                          onClick={() => {
                            setEditingSeats(false);
                            setLastUpdatedSeat(null);
                            // Reset edited values to current values
                            const resetEdited = {};
                            bookingSeats.forEach(seat => {
                              const seatId = seat.bookingSeatID || seat.BookingSeatID || seat.id;
                              const seatNum = seat.seatNumber || seat.SeatNumber || '';
                              if (seatId) {
                                resetEdited[seatId] = seatNum.toString();
                              }
                            });
                            setEditedSeatNumbers(resetEdited);
                          }}
                          className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {bookingSeats.map((bookingSeat, index) => {
                          const seatNumber = bookingSeat.seatNumber || bookingSeat.SeatNumber || 'N/A';
                          return (
                            <span
                              key={bookingSeat.bookingSeatID || bookingSeat.BookingSeatID || bookingSeat.id || index}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg font-medium text-sm"
                            >
                              {seatNumber}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Send SMS Button - Show after seat is updated */}
                    {lastUpdatedSeat && lastUpdatedSeat.bookingSeatId && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleSendSeatChangeSms(
                            lastUpdatedSeat.bookingSeatId,
                            lastUpdatedSeat.oldSeatNumber,
                            lastUpdatedSeat.newSeatNumber
                          )}
                          disabled={sendingSms}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                        >
                          {sendingSms ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending SMS...
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Send SMS (Seat {lastUpdatedSeat.oldSeatNumber} → {lastUpdatedSeat.newSeatNumber})
                            </>
                          )}
                        </button>
                        <p className="mt-2 text-xs text-gray-500 text-center">
                          Notify customer about seat change
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Download Receipt Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      const bookingId = bookingDetails.bookingID || bookingDetails.BookingID;
                      if (bookingId) {
                        handleViewReceipt(bookingId);
                      }
                    }}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Receipt
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No booking details available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Check Payment Modal */}
      {showCheckPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <CreditCard className="w-6 h-6" />
                <span>Check Payment Status</span>
              </h3>
              <button
                onClick={() => {
                  setShowCheckPaymentModal(false);
                  setPaymentCheckResults([]);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Booking List */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Bookings to Check
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {filteredBookings.length > 0 ? (
                    <div className="space-y-2">
                      {filteredBookings.map((booking) => {
                        const bookingId = booking.bookingID || booking.bookingId || booking.id;
                        const bookingRef = booking.bookingReference || `BK${String(bookingId).padStart(6, '0')}`;
                        const isChecked = paymentCheckResults.some(r => r.bookingId === bookingId);
                        
                        return (
                          <label
                            key={bookingId}
                            className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Add to results list
                                  setPaymentCheckResults(prev => [...prev, {
                                    bookingId: bookingId,
                                    bookingRef: bookingRef,
                                    customerName: booking.customerName || booking.customer?.customerName || 'N/A',
                                    amount: booking.totalAmount || booking.totalamount || 0,
                                    checking: false,
                                    result: null,
                                    error: null
                                  }]);
                                } else {
                                  // Remove from results list
                                  setPaymentCheckResults(prev => prev.filter(r => r.bookingId !== bookingId));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{bookingRef}</div>
                              <div className="text-sm text-gray-500">
                                {booking.customerName || booking.customer?.customerName || 'N/A'} - 
                                Rs. {(booking.totalAmount || booking.totalamount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No bookings available. Please adjust your filters.
                    </div>
                  )}
                </div>
              </div>

              {/* Check Button */}
              {paymentCheckResults.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={async () => {
                      setCheckingPayments(true);
                      const results = [...paymentCheckResults];
                      
                      for (let i = 0; i < results.length; i++) {
                        const result = results[i];
                        result.checking = true;
                        result.result = null;
                        result.error = null;
                        setPaymentCheckResults([...results]);
                        
                        try {
                          const response = await paymentApi.checkPaymentStatus(result.bookingId);
                          const data = response?.value || response?.data || response;
                          
                          result.checking = false;
                          result.result = data;
                          result.error = null;
                        } catch (error) {
                          result.checking = false;
                          result.result = null;
                          result.error = error.response?.data?.message || error.message || 'Failed to check payment status';
                        }
                        
                        setPaymentCheckResults([...results]);
                      }
                      
                      setCheckingPayments(false);
                    }}
                    disabled={checkingPayments}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {checkingPayments ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Checking Payments...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Check Selected Payments ({paymentCheckResults.length})</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Results */}
              {paymentCheckResults.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">Payment Status Results</h4>
                  {paymentCheckResults.map((result, index) => (
                    <div
                      key={result.bookingId}
                      className="border border-gray-300 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-medium text-gray-900">{result.bookingRef}</div>
                          <div className="text-sm text-gray-500">{result.customerName}</div>
                        </div>
                        {result.checking ? (
                          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        ) : result.error ? (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        ) : result.result ? (
                          result.result.isPaymentSuccessful ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )
                        ) : null}
                      </div>
                      
                      {result.checking && (
                        <div className="text-sm text-gray-500">Checking payment status...</div>
                      )}
                      
                      {result.error && (
                        <div className="text-sm text-red-600 mt-2">
                          <strong>Error:</strong> {result.error}
                        </div>
                      )}
                      
                      {result.result && (
                        <div className="mt-3 space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">Status:</span>
                            <span className={`text-sm font-semibold ${
                              result.result.isPaymentSuccessful ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {result.result.paymentStatusMessage || result.result.paymentStatus || 'Unknown'}
                            </span>
                          </div>
                          {result.result.amount > 0 && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Amount:</span>
                              <span className="text-sm text-gray-900">
                                {result.result.currency} {result.result.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          )}
                          {result.result.result && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Result:</span>
                              <span className="text-sm text-gray-900">{result.result.result}</span>
                            </div>
                          )}
                          {result.result.authenticationStatus && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Authentication:</span>
                              <span className="text-sm text-gray-900">{result.result.authenticationStatus}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCheckPaymentModal(false);
                  setPaymentCheckResults([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Status Details Modal */}
      {showGatewayStatusModal && selectedGatewayStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <CreditCard className="w-6 h-6" />
                <span>Payment Gateway Status Details</span>
              </h3>
              <button
                onClick={() => {
                  setShowGatewayStatusModal(false);
                  setSelectedGatewayStatus(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedGatewayStatus.details ? (
                <div className="space-y-4">
                  {/* Booking Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Booking Information</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Booking Reference</p>
                        <p className="text-sm font-medium text-gray-900">{selectedGatewayStatus.bookingRef}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Customer Name</p>
                        <p className="text-sm font-medium text-gray-900">{selectedGatewayStatus.customerName}</p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Payment Status</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          selectedGatewayStatus.status === 'Success'
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {selectedGatewayStatus.status}
                        </span>
                      </div>
                      {selectedGatewayStatus.details.paymentStatus && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Payment Status:</span>
                          <span className="text-sm text-gray-900">{selectedGatewayStatus.details.paymentStatus}</span>
                        </div>
                      )}
                      {selectedGatewayStatus.details.paymentStatusMessage && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Status Message:</span>
                          <span className="text-sm text-gray-900">{selectedGatewayStatus.details.paymentStatusMessage}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Amount Information */}
                  {selectedGatewayStatus.details.amount > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Amount Information</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Amount:</span>
                          <span className="text-lg font-bold text-green-600">
                            {selectedGatewayStatus.details.currency || 'LKR'} {selectedGatewayStatus.details.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        {selectedGatewayStatus.details.merchantAmount && selectedGatewayStatus.details.merchantAmount !== selectedGatewayStatus.details.amount && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Merchant Amount:</span>
                            <span className="text-sm text-gray-900">
                              {selectedGatewayStatus.details.merchantCurrency || selectedGatewayStatus.details.currency || 'LKR'} {selectedGatewayStatus.details.merchantAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Details */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Details</h4>
                    <div className="space-y-2">
                      {selectedGatewayStatus.details.result && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Result:</span>
                          <span className={`text-sm font-semibold ${
                            selectedGatewayStatus.details.result === 'SUCCESS' 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {selectedGatewayStatus.details.result}
                          </span>
                        </div>
                      )}
                      {selectedGatewayStatus.details.authenticationStatus && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Authentication Status:</span>
                          <span className="text-sm text-gray-900">{selectedGatewayStatus.details.authenticationStatus}</span>
                        </div>
                      )}
                      {selectedGatewayStatus.details.orderId && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Order ID:</span>
                          <span className="text-sm text-gray-900">{selectedGatewayStatus.details.orderId}</span>
                        </div>
                      )}
                      {selectedGatewayStatus.details.creationTime && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Creation Time:</span>
                          <span className="text-sm text-gray-900">
                            {new Date(selectedGatewayStatus.details.creationTime).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {selectedGatewayStatus.details.lastUpdatedTime && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Last Updated:</span>
                          <span className="text-sm text-gray-900">
                            {new Date(selectedGatewayStatus.details.lastUpdatedTime).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : selectedGatewayStatus.error ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-900 mb-2">Error Checking Payment Status</p>
                  <p className="text-sm text-gray-600">{selectedGatewayStatus.error}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
                  <p className="text-sm text-gray-600">Loading payment status...</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowGatewayStatusModal(false);
                  setSelectedGatewayStatus(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManagerDashboard;
